<template>
  <div v-if="isAuthenticated" class="relative min-h-screen overflow-hidden">
    <!-- Background Image with multiply blend effect -->
    <div class="fixed inset-0 -z-30">
      <img src="/assets/photo.jpg" alt="Background" class="w-full h-full object-cover opacity-50 bg-image-animate" />
    </div>
    
    <!-- Primary multiply blend overlay for rich color interaction -->
    <div class="fixed inset-0 -z-20 bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"></div>
    
    <!-- Secondary overlay for better text readability -->
    <div class="fixed inset-0 -z-10 bg-black/40 backdrop-blur-[2px]"></div>
    
    <!-- Additional dark base layer -->
    <div class="fixed inset-0 bg-gray-900" style="z-index: -40;"></div>

    <!-- Navigation Header -->
    <div class="fixed top-0 left-0 right-0 z-20 px-2 sm:px-4 py-2 sm:py-3">
      <div class="max-w-7xl mx-auto">
        <div class="rounded-full px-3 sm:px-6 py-2 sm:py-3 flex justify-between items-center bg-black/60 backdrop-blur-lg shadow-md border border-white/20">
          <div class="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <img src="/assets/engagr-wh.svg" alt="Engagr" class="h-6 sm:h-8 flex-shrink-0" />
            <div class="h-4 sm:h-6 w-px bg-white/30 hidden sm:block"></div>
            <span class="text-white font-medium text-xs sm:text-sm md:text-base hidden sm:inline truncate">
              Agency Admin Dashboard
            </span>
            <div v-if="currentUser" class="hidden md:flex items-center space-x-2 ml-4 pl-4 border-l border-white/30">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span class="text-white/90 text-xs sm:text-sm font-medium">
                {{ currentUser.name || currentUser.email }}
              </span>
            </div>
          </div>
          <div class="flex items-center space-x-1.5 sm:space-x-2">
            <button
              @click="showUserManagement = true"
              class="inline-flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 rounded-full transition-all duration-300 text-white text-sm font-medium"
              title="Manage Users"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span class="hidden sm:inline">Users</span>
            </button>
            <button
              @click="handleLogout"
              class="inline-flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-4 sm:py-2 bg-red-600 hover:bg-red-700 rounded-full transition-all duration-300 text-white text-sm font-medium"
              title="Sign Out"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span class="hidden sm:inline">Sign Out</span>
            </button>
            <router-link
              to="/"
              class="inline-flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-4 sm:py-2 bg-gray-700 hover:bg-gray-600 rounded-full transition-all duration-300 text-white text-sm font-medium"
              title="Back to Home"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span class="hidden sm:inline">Back to Home</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Spacer for navbar -->
    <div class="h-16 sm:h-20"></div>

    <!-- Dashboard Header -->
    <div class="relative z-1 pt-8 pb-4">
      <div class="max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto">
        <div class="text-center mb-8">
          <div class="mb-3 inline-block px-4 py-1">
            <p class="text-blue-400 font-medium">Agency Performance Portal</p>
          </div>
          <h1
            class="text-3xl md:text-5xl font-bold text-white mb-3 bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent"
          >
            Admin Dashboard
          </h1>
          <p class="text-gray-300 text-lg mb-3">
            Overall product performance across all accounts
          </p>
        </div>
      </div>
    </div>

    <!-- Date Range Presets - Global Filters -->
    <div
      v-if="!isLoading && !error && stats"
      class="max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto relative z-1 mb-8"
    >
      <div class="flex flex-wrap justify-center gap-2 lg:gap-3">
        <button
          @click="applyDateFilter('all')"
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
          :class="
            dateFilter === 'all'
              ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
              : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
          "
        >
          <svg
            class="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          <span>All Time</span>
        </button>

        <button
          @click="applyDateFilter('last7')"
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
          :class="
            dateFilter === 'last7'
              ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
              : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
          "
        >
          <svg
            class="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>Last 7 days</span>
        </button>

        <button
          @click="applyDateFilter('last30')"
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
          :class="
            dateFilter === 'last30'
              ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
              : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
          "
        >
          <svg
            class="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>Last 30 days</span>
        </button>

        <button
          @click="applyDateFilter('thisMonth')"
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
          :class="
            dateFilter === 'thisMonth'
              ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
              : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
          "
        >
          <svg
            class="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>This month</span>
        </button>

        <button
          @click="applyDateFilter('lastMonth')"
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
          :class="
            dateFilter === 'lastMonth'
              ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
              : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
          "
        >
          <svg
            class="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>Last month</span>
        </button>

        <button
          @click="applyDateFilter('last12Months')"
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0"
          :class="
            dateFilter === 'last12Months'
              ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-400 shadow-lg shadow-blue-500/20'
              : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60 hover:border-white/20 hover:text-white'
          "
        >
          <svg
            class="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>12 months</span>
        </button>
      </div>
    </div>

    <!-- Loader with Progress -->
    <div
      v-if="isLoading"
      class="flex flex-col justify-center items-center min-h-[400px] space-y-6"
    >
      <div role="status" class="relative">
        <svg
          aria-hidden="true"
          class="w-20 h-20 text-gray-600 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
      
      <!-- Loading Status Messages -->
      <div class="text-center space-y-3 max-w-md">
        <p class="text-white text-lg font-medium">
          {{ loadingMessage }}
        </p>
        <p class="text-gray-400 text-sm">
          {{ loadingSubMessage }}
        </p>
        
        <!-- Performance Tip -->
        <div class="mt-6 p-4 bg-blue-900/40 border border-blue-500/30 rounded-xl">
          <p class="text-blue-200 text-xs flex items-center justify-center">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            This data is cached for 10 minutes for faster subsequent loads
          </p>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-if="error && !isLoading"
      class="max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto relative z-1"
    >
      <div class="bg-red-900/60 backdrop-blur-lg border border-red-500/50 rounded-3xl p-8 text-center">
        <svg class="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 class="text-2xl font-bold text-white mb-2">Error Loading Data</h3>
        <p class="text-red-200 mb-4">{{ error }}</p>
        <button
          @click="fetchStats"
          class="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-full text-white font-medium transition-all"
        >
          Retry
        </button>
      </div>
    </div>

    <!-- Stale / Partial Data Warning -->
    <!-- The backend returns HTTP 200 with stale cache + syncError when aggregation
         fails or times out, and sets partial=true when it hits its 22s deadline.
         Without this banner those failures render as confident (often zero) numbers. -->
    <div
      v-if="!isLoading && !error && stats && (stats.syncError || stats.partial)"
      class="max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto relative z-1 mb-6"
    >
      <div class="bg-amber-900/50 backdrop-blur-lg border border-amber-500/50 rounded-2xl p-5 flex items-start gap-4">
        <svg class="w-6 h-6 text-amber-300 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        <div class="flex-1 text-left">
          <h4 class="text-amber-100 font-semibold mb-1">
            {{ stats.syncError ? 'These numbers are out of date' : 'These numbers are incomplete' }}
          </h4>
          <p class="text-amber-200/90 text-sm">
            <template v-if="stats.syncError">
              The latest refresh failed, so cached data is being shown.
              <span class="text-amber-300/80">({{ stats.syncError }})</span>
            </template>
            <template v-else>
              The refresh ran out of time and only some accounts were counted. Totals are undercounted.
            </template>
          </p>
        </div>
        <button
          @click="forceRefresh"
          class="px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded-full text-white text-sm font-medium transition-all flex-shrink-0"
        >
          Retry
        </button>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div
      v-if="!isLoading && !error && stats"
      class="max-w-7xl px-4 sm:px-6 lg:px-8 mx-auto relative z-1 pb-12"
    >
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2 group relative">
              <h3 class="text-gray-400 text-sm font-medium">Total Accounts</h3>
              <div class="tooltip-trigger cursor-help">
                <svg class="w-4 h-4 text-gray-500 hover:text-gray-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div class="tooltip-content">
                  Count of all unique forms/accounts in Gravity Forms
                </div>
              </div>
            </div>
            <div class="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
          <p class="text-4xl font-bold text-white mb-4">{{ filteredAccountCount }}</p>
          
          <!-- Activity Insights -->
          <div class="space-y-3 pt-4 border-t border-white/10">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <div class="w-2 h-2 rounded-full bg-green-400"></div>
                <span class="text-gray-400 text-xs font-medium">Active ({{ dateFilter === 'all' ? 'all time' : getDateFilterLabel().toLowerCase() }})</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-white font-semibold text-sm">{{ filteredAccountActivityStats.active }}</span>
                <span class="text-gray-500 text-xs">({{ filteredAccountActivityStats.activePercentage }}%)</span>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <div class="w-2 h-2 rounded-full bg-red-500"></div>
                <span class="text-gray-400 text-xs font-medium">Inactive ({{ dateFilter === 'all' ? 'all time' : getDateFilterLabel().toLowerCase() }})</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-white font-semibold text-sm">{{ filteredAccountActivityStats.inactive }}</span>
                <span class="text-gray-500 text-xs">({{ filteredAccountActivityStats.inactivePercentage }}%)</span>
              </div>
            </div>
            
            <!-- Days Since Last Activity -->
            <div v-if="filteredAccountActivityStats.inactive > 0 && filteredAccountActivityStats.avgDaysSinceLastActivity > 0" class="pt-2 border-t border-white/5">
              <div class="flex items-center justify-between mb-2">
                <span class="text-gray-400 text-xs font-medium">Avg. days inactive</span>
                <span class="text-white font-semibold text-sm">{{ filteredAccountActivityStats.avgDaysSinceLastActivity }} days</span>
              </div>
              <div class="grid grid-cols-4 gap-1 text-xs">
                <div class="text-center">
                  <div class="text-gray-500">1-30d</div>
                  <div class="text-white font-medium">{{ filteredAccountActivityStats.inactiveAccountsBreakdown.recent }}</div>
                </div>
                <div class="text-center">
                  <div class="text-gray-500">31-60d</div>
                  <div class="text-white font-medium">{{ filteredAccountActivityStats.inactiveAccountsBreakdown.moderate }}</div>
                </div>
                <div class="text-center">
                  <div class="text-gray-500">61-90d</div>
                  <div class="text-white font-medium">{{ filteredAccountActivityStats.inactiveAccountsBreakdown.long }}</div>
                </div>
                <div class="text-center">
                  <div class="text-gray-500">90+d</div>
                  <div class="text-white font-medium">{{ filteredAccountActivityStats.inactiveAccountsBreakdown.veryLong }}</div>
                </div>
              </div>
            </div>
            
            <!-- Visual Progress Bar -->
            <div class="mt-3 h-2 bg-black/40 rounded-full overflow-hidden">
              <div class="h-full flex">
                <div 
                  v-if="filteredAccountActivityStats.activePercentage > 0"
                  class="bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500"
                  :style="{ width: `${filteredAccountActivityStats.activePercentage}%` }"
                ></div>
                <div 
                  v-if="filteredAccountActivityStats.inactivePercentage > 0"
                  class="bg-gray-600 transition-all duration-500"
                  :style="{ width: `${filteredAccountActivityStats.inactivePercentage}%` }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2 group relative">
              <h3 class="text-gray-400 text-sm font-medium">Total Submissions</h3>
              <div class="tooltip-trigger cursor-help">
                <svg class="w-4 h-4 text-gray-500 hover:text-gray-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div class="tooltip-content">
                  Sum of all form submissions across all accounts since the beginning
                </div>
              </div>
            </div>
            <div class="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          <p class="text-4xl font-bold text-white mb-4">{{ formatNumber(filteredSummaryStats.totalSubmissions) }}</p>
          
          <!-- Submission Concentration Insights -->
          <div class="space-y-3 pt-4 border-t border-white/10">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span class="text-gray-400 text-xs font-medium">Top 20% accounts</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-white font-semibold text-sm">{{ submissionConcentration.top20Percent }}%</span>
                <span 
                  :class="[
                    'px-1.5 py-0.5 rounded text-xs font-medium',
                    submissionConcentration.concentrationRisk === 'high' ? 'bg-red-500/20 text-red-400' :
                    submissionConcentration.concentrationRisk === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-green-500/20 text-green-400'
                  ]"
                >
                  {{ submissionConcentration.concentrationRisk }}
                </span>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <span class="text-gray-400 text-xs font-medium">Top 5 accounts</span>
              </div>
              <span class="text-white font-semibold text-sm">{{ submissionConcentration.top5Accounts }}%</span>
            </div>
            
            <!-- Concentration Risk Indicator -->
            <div class="mt-3 pt-3 border-t border-white/5">
              <div class="flex items-center justify-between">
                <span class="text-gray-400 text-xs">Concentration risk</span>
                <div class="flex items-center gap-1">
                  <div 
                    v-for="i in 3" 
                    :key="i"
                    :class="[
                      'w-2 h-2 rounded-full',
                      i <= (submissionConcentration.concentrationRisk === 'high' ? 3 : submissionConcentration.concentrationRisk === 'medium' ? 2 : 1)
                        ? 'bg-blue-400'
                        : 'bg-gray-600'
                    ]"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2 group relative">
              <h3 class="text-gray-400 text-sm font-medium">Average per Account</h3>
              <div class="tooltip-trigger cursor-help">
                <svg class="w-4 h-4 text-gray-500 hover:text-gray-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div class="tooltip-content">
                  Total Submissions ÷ Total Accounts (all-time average)
                </div>
              </div>
            </div>
            <div class="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <svg class="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
          <p class="text-4xl font-bold text-white mb-4">{{ filteredSummaryStats.averageSubmissions }}</p>
          
          <!-- Average Insights -->
          <div class="space-y-3 pt-4 border-t border-white/10">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span class="text-gray-400 text-xs font-medium">Median</span>
              </div>
              <span class="text-white font-semibold text-sm">{{ averageInsights.median }}</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
                <span class="text-gray-400 text-xs font-medium">Range</span>
              </div>
              <span class="text-white font-semibold text-sm">{{ averageInsights.min }} - {{ formatNumber(averageInsights.max) }}</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span class="text-gray-400 text-xs font-medium">Above average</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-white font-semibold text-sm">{{ averageInsights.aboveAverage }}</span>
                <span class="text-gray-500 text-xs">({{ averageInsights.aboveAveragePercentage }}%)</span>
              </div>
            </div>
            
            <!-- Distribution Breakdown -->
            <div class="pt-2 border-t border-white/5">
              <div class="grid grid-cols-4 gap-1 text-xs mb-2">
                <div class="text-center">
                  <div class="text-gray-500">0-25</div>
                  <div class="text-white font-medium">{{ averageInsights.distribution.zeroTo25 }}</div>
                </div>
                <div class="text-center">
                  <div class="text-gray-500">26-50</div>
                  <div class="text-white font-medium">{{ averageInsights.distribution.twentySixTo50 }}</div>
                </div>
                <div class="text-center">
                  <div class="text-gray-500">51-100</div>
                  <div class="text-white font-medium">{{ averageInsights.distribution.fiftyOneTo100 }}</div>
                </div>
                <div class="text-center">
                  <div class="text-gray-500">100+</div>
                  <div class="text-white font-medium">{{ averageInsights.distribution.over100 }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Engagement Chart -->
      <div class="mb-8">
        <EngagementChart 
          :accounts="globalFilteredAccounts" 
          :date-filter="dateFilter"
          :get-filtered-submissions="getFilteredSubmissions"
        />
      </div>

      <!-- Account Health Trends -->
      <div v-if="accountHealthTrends.growing.length > 0 || accountHealthTrends.declining.length > 0 || accountHealthTrends.newAccounts.length > 0" class="mb-8">
        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl p-6 shadow-xl overflow-hidden">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h3 class="text-xl font-bold text-white mb-1">Account Health Trends</h3>
              <p class="text-gray-400 text-xs font-medium">Accounts showing growth, decline, or new activity</p>
            </div>
            <div class="w-12 h-12 bg-gradient-to-br from-green-500/30 to-blue-500/30 rounded-xl flex items-center justify-center shadow-lg border border-green-500/20">
              <svg class="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- Growing Accounts -->
            <AccountHealthCard
              title="Growing"
              :accounts="accountHealthTrends.growing"
              bg-gradient="from-green-500/10 to-green-600/5"
              border-color="border-green-500/20"
              icon-color="text-green-400"
              badge-bg="bg-green-500/20"
              badge-text="text-green-400"
              value-color="text-green-400"
              key-prefix="growing"
            >
              <template #icon>
                <svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </template>
              <template #value="{ account }">
                +{{ getFilteredSubmissions(account) }} submissions
              </template>
            </AccountHealthCard>

            <!-- Declining Accounts -->
            <AccountHealthCard
              title="Declining"
              :accounts="accountHealthTrends.declining"
              bg-gradient="from-red-500/10 to-red-600/5"
              border-color="border-red-500/20"
              icon-color="text-red-400"
              badge-bg="bg-red-500/20"
              badge-text="text-red-400"
              value-color="text-red-400"
              key-prefix="declining"
            >
              <template #icon>
                <svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              </template>
              <template #value="{ account }">
                {{ getFilteredSubmissions(account) }} submissions (↓)
              </template>
            </AccountHealthCard>

            <!-- New Accounts -->
            <AccountHealthCard
              title="New"
              :accounts="accountHealthTrends.newAccounts"
              bg-gradient="from-blue-500/10 to-blue-600/5"
              border-color="border-blue-500/20"
              icon-color="text-blue-400"
              badge-bg="bg-blue-500/20"
              badge-text="text-blue-400"
              value-color="text-blue-400"
              key-prefix="new"
            >
              <template #icon>
                <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </template>
            </AccountHealthCard>

            <!-- Stable Accounts -->
            <AccountHealthCard
              title="Stable"
              :accounts="accountHealthTrends.stable"
              bg-gradient="from-gray-500/10 to-gray-600/5"
              border-color="border-gray-500/20"
              icon-color="text-gray-400"
              badge-bg="bg-gray-500/20"
              badge-text="text-gray-400"
              value-color="text-gray-400"
              key-prefix="stable"
            >
              <template #icon>
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14" />
                </svg>
              </template>
            </AccountHealthCard>
          </div>
        </div>
      </div>

      <!-- Top Accounts Widget -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <!-- Top Accounts by Total Submissions -->
        <AccountListCard
          title="Top Accounts"
          subtitle="All Time Performance"
          :accounts="topAccountsByTotal"
          value-key="totalSubmissions"
          value-label="submissions"
          gradient-color="bg-blue-500/5"
          icon-bg-class="bg-gradient-to-br from-blue-500/30 to-blue-500/30"
          icon-border-class="border border-blue-500/20"
          icon-color-class="text-blue-400"
          hover-text-color="text-blue-200"
          key-prefix="total"
        >
          <template #icon>
            <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </template>
        </AccountListCard>

        <!-- Top Accounts by Filter Period -->
        <AccountListCard
          title="Top Accounts"
          :subtitle="getDateFilterLabel() + ' Activity'"
          :accounts="topAccountsBy30Days"
          value-key="filteredSubmissions"
          value-label="submissions"
          gradient-color="bg-orange-500/5"
          icon-bg-class="bg-gradient-to-br from-orange-500/30 to-yellow-500/30"
          icon-border-class="border border-orange-500/20"
          icon-color-class="text-orange-400"
          hover-text-color="text-orange-200"
          key-prefix="30days"
        >
          <template #icon>
            <svg class="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </template>
        </AccountListCard>
      </div>

      <!-- Account Management Section -->
      <div class="mb-8">
        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-xl overflow-hidden">
          <div class="px-6 py-4 border-b border-white/10">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-xl font-bold text-white mb-1">Account Management</h2>
                <p class="text-gray-400 text-sm">Manage account settings, view details, and perform bulk actions</p>
              </div>
              <div class="flex items-center gap-2">
                <button
                  @click="showAccountManagement = !showAccountManagement"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-full text-white text-sm font-medium transition-all flex items-center"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {{ showAccountManagement ? 'Hide' : 'Show' }} Management
                </button>
              </div>
            </div>
          </div>

          <div v-if="showAccountManagement" class="p-6">
            <!-- Quick Stats -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div 
                @click="statusFilter = 'all'; currentPage = 1"
                class="bg-white/5 rounded-xl p-4 border border-white/10 cursor-pointer hover:bg-white/10 transition-all"
                :class="statusFilter === 'all' ? 'ring-2 ring-blue-500' : ''"
              >
                <div class="text-gray-400 text-xs font-medium mb-1">Total Accounts</div>
                <div class="text-white text-2xl font-bold">{{ managementAccounts.length || 0 }}</div>
              </div>
              <div 
                @click="statusFilter = 'active'; currentPage = 1"
                class="bg-white/5 rounded-xl p-4 border border-white/10 cursor-pointer hover:bg-white/10 transition-all"
                :class="statusFilter === 'active' ? 'ring-2 ring-green-500' : ''"
              >
                <div class="text-gray-400 text-xs font-medium mb-1">Active Accounts</div>
                <div class="text-green-400 text-2xl font-bold">{{ accountActivityStats.active }}</div>
              </div>
              <div 
                @click="statusFilter = 'inactive'; currentPage = 1"
                class="bg-white/5 rounded-xl p-4 border border-white/10 cursor-pointer hover:bg-white/10 transition-all"
                :class="statusFilter === 'inactive' ? 'ring-2 ring-red-500' : ''"
              >
                <div class="text-gray-400 text-xs font-medium mb-1">Inactive Accounts</div>
                <div class="text-red-400 text-2xl font-bold">{{ accountActivityStats.inactive }}</div>
              </div>
              <div class="bg-white/5 rounded-xl p-4 border border-white/10">
                <div class="text-gray-400 text-xs font-medium mb-1">With Dashboard Links</div>
                <div class="text-blue-400 text-2xl font-bold">{{ accountsWithDashboardLinks }}</div>
              </div>
            </div>

            <!-- Search Field -->
            <div class="mb-6">
              <div class="relative w-full">
                <input
                  v-model="managementSearchQuery"
                  type="text"
                  placeholder="Search by account name..."
                  class="w-full px-4 py-2 pl-10 bg-black/60 border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  style="background-color: rgba(0, 0, 0, 0.6); color: white;"
                />
                <svg class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div v-if="statusFilter !== 'all'" class="mt-2 flex items-center gap-2">
                <span class="text-sm text-gray-400">Filtered by:</span>
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      :class="statusFilter === 'active' ? 'bg-green-500/20 text-green-300 border border-green-400/30' : 'bg-red-500/20 text-red-300 border border-red-400/30'">
                  {{ statusFilter === 'active' ? 'Active' : 'Inactive' }}
                </span>
                <button
                  @click="statusFilter = 'all'; currentPage = 1"
                  class="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Clear filter
                </button>
              </div>
            </div>

            <!-- Account List with Management Actions -->
            <div class="space-y-3">
              <div v-if="filteredManagementAccounts.length === 0" class="text-center py-8">
                <svg class="w-12 h-12 text-gray-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p class="text-gray-400">No accounts found matching your search.</p>
              </div>
              <div
                v-for="account in filteredManagementAccounts"
                :key="account.formId"
                class="bg-white/5 rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-all"
              >
                <div class="flex items-center justify-between">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-3 mb-2">
                      <h3 class="text-white font-semibold text-base truncate">
                        {{ account.accountName || account.formTitle || `Form ${account.formId}` }}
                      </h3>
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        :class="getCachedAccountStatus(account) === 'active' ? 'bg-green-500/20 text-green-300 border border-green-400/30' : 'bg-red-500/20 text-red-300 border border-red-400/30'"
                      >
                        {{ getCachedAccountStatus(account) === 'active' ? 'Active' : 'Inactive' }}
                      </span>
                    </div>
                    <div class="flex items-center gap-4 text-sm text-gray-400">
                      <span>Form ID: {{ account.formId }}</span>
                      <span>Total: {{ formatNumber(account.totalSubmissions) }} submissions</span>
                      <span v-if="account.lastSubmission">
                        Last: {{ formatDate(account.lastSubmission) }}
                      </span>
                      <span v-else class="text-gray-500">No submissions</span>
                    </div>
                  </div>
                  <div class="flex items-center gap-2 ml-4">
                    <button
                      v-if="account.accountSlug"
                      @click="viewAccountDetails(account)"
                      class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-xs font-medium transition-all flex items-center"
                      title="View Account Details"
                    >
                      <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Details
                    </button>
                    <router-link
                      v-if="account.accountSlug"
                      :to="`/account/${account.accountSlug}/dashboard`"
                      target="_blank"
                      class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-xs font-medium transition-all flex items-center"
                      title="View Dashboard"
                    >
                      <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Dashboard
                    </router-link>
                    <button
                      @click="editAccount(account)"
                      class="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 rounded-lg text-white text-xs font-medium transition-all flex items-center"
                      title="Edit Account"
                    >
                      <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  </div>
                </div>
              </div>
              
              <div v-if="filteredManagementAccounts.length > 0" class="text-center pt-4 border-t border-white/10 mt-4 pt-4">
                <p class="text-gray-400 text-sm mb-3">
                  Showing {{ filteredManagementAccounts.length }} of {{ managementAccounts.length }} accounts
                  <span v-if="statusFilter === 'all'">({{ accountActivityStats.inactive }} inactive, {{ accountActivityStats.active }} active)</span>
                  <span v-else-if="statusFilter === 'inactive'">(inactive only)</span>
                  <span v-else>(active only)</span>
                  <span v-if="statusFilter === 'all'">. Inactive accounts are shown first.</span>
                </p>
                <button
                  @click="scrollToAccountsTable"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-full text-white text-sm font-medium transition-all"
                >
                  View Accounts Table
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Accounts Table -->
      <div id="accounts-table" class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-xl overflow-hidden">
        <div class="px-6 py-4 border-b border-white/10">
          <div class="flex flex-col gap-4">
            <!-- Title -->
            <h2 class="text-xl font-bold text-white">Account Performance</h2>
            
            <!-- Controls: Search bar on top, buttons below -->
            <div class="flex flex-col gap-3">
              <!-- Top Row: Search Bar (full width) -->
              <div class="relative w-full">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search by account name..."
                  class="w-full px-4 py-2 pl-10 bg-black/60 border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  style="background-color: rgba(0, 0, 0, 0.6); color: white;"
                />
                <svg class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              
              <!-- Bottom Row: Status Badge and Buttons -->
              <div class="flex flex-wrap items-center gap-3">
                <!-- Refresh Button -->
                <button
                  @click="fetchStats(true)"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-full text-white text-sm font-medium transition-all flex items-center whitespace-nowrap flex-shrink-0"
                  title="Refresh Data"
                >
                  <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
                
                <!-- Status Badge -->
                <div v-if="stats.cached && !isSyncing && !stats.stale" class="inline-flex items-center px-3 py-2 bg-green-900/40 border border-green-500/30 rounded-full flex-shrink-0">
                  <svg class="w-4 h-4 text-green-400 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span class="text-green-200 text-xs font-medium whitespace-nowrap">
                    Last updated: {{ formatLastSyncTime() }}
                  </span>
                </div>
                
                <!-- Syncing Badge -->
                <div v-if="isSyncing" class="inline-flex items-center px-3 py-2 bg-blue-900/40 border border-blue-500/30 rounded-full flex-shrink-0">
                  <svg class="w-4 h-4 text-blue-400 mr-1.5 animate-spin flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span class="text-blue-200 text-xs font-medium whitespace-nowrap">
                    Updating...
                  </span>
                </div>
                
                <!-- Stale Data Badge -->
                <div v-if="stats.stale && !isSyncing && stats.cached" class="inline-flex items-center px-3 py-2 bg-yellow-900/40 border border-yellow-500/30 rounded-full flex-shrink-0">
                  <svg class="w-4 h-4 text-yellow-400 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span class="text-yellow-200 text-xs font-medium whitespace-nowrap">
                    Data may be outdated
                  </span>
                  <button
                    @click="forceRefresh"
                    class="ml-2 text-xs text-yellow-300 hover:text-yellow-100 underline whitespace-nowrap"
                  >
                    Update Now
                  </button>
                </div>
                
                <!-- Download Report Button -->
                <button
                  @click="downloadMonthlyReport"
                  :disabled="isGeneratingReport || isSendingEmail || isSendingSlack"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-full text-white text-sm font-medium transition-all flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  title="Download Monthly Report"
                >
                  <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-if="!isGeneratingReport">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <svg class="w-4 h-4 mr-2 animate-spin flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {{ isGeneratingReport ? 'Generating...' : 'Download Report' }}
                </button>
                
                <!-- Report Settings Button -->
                <button
                  @click="previewEmailReport"
                  :disabled="isGeneratingReport || isSendingEmail || isSendingSlack"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-full text-white text-sm font-medium transition-all flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  title="Configure Email Reports & Settings"
                >
                  <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Report Settings
                </button>
                
                <!-- Email Report Button -->
                <button
                  @click="emailMonthlyReport"
                  :disabled="isGeneratingReport || isSendingEmail || isSendingSlack"
                  class="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-full text-white text-sm font-medium transition-all flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  :title="emailRecipientsTooltip"
                >
                  <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-if="!isSendingEmail">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <svg class="w-4 h-4 mr-2 animate-spin flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {{ isSendingEmail ? 'Sending...' : 'Email Report' }}
                </button>
                
                <!-- Send to Slack Button -->
                <button
                  @click="sendSlackReport"
                  :disabled="isGeneratingReport || isSendingEmail || isSendingSlack"
                  class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-full text-white text-sm font-medium transition-all flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  title="Send Report to Slack"
                >
                  <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-if="!isSendingSlack">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <svg class="w-4 h-4 mr-2 animate-spin flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {{ isSendingSlack ? 'Sending...' : 'Send to Slack' }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-black/40">
              <tr>
                <th 
                  @click="sortTable('accountName')"
                  class="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-black/60 transition-colors select-none"
                >
                  <div class="flex items-center gap-2">
                    <span>Account Name</span>
                    <div class="flex flex-col">
                      <svg 
                        :class="['w-3 h-3', sortColumn === 'accountName' && sortDirection === 'asc' ? 'text-blue-400' : 'text-gray-600']"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                      </svg>
                      <svg 
                        :class="['w-3 h-3 -mt-1', sortColumn === 'accountName' && sortDirection === 'desc' ? 'text-blue-400' : 'text-gray-600']"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </th>
                <th 
                  @click="sortTable('totalSubmissions')"
                  class="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-black/60 transition-colors select-none"
                >
                  <div class="flex flex-col items-end">
                    <div class="flex items-center gap-2">
                      <span>Total Submissions</span>
                      <div class="flex flex-col">
                        <svg 
                          :class="['w-3 h-3', sortColumn === 'totalSubmissions' && sortDirection === 'asc' ? 'text-blue-400' : 'text-gray-600']"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                        </svg>
                        <svg 
                          :class="['w-3 h-3 -mt-1', sortColumn === 'totalSubmissions' && sortDirection === 'desc' ? 'text-blue-400' : 'text-gray-600']"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    <span class="text-xs font-normal normal-case text-gray-500 mt-0.5">{{ dateFilter === 'all' ? 'Current Year' : 'All Time' }}</span>
                  </div>
                </th>
                <th 
                  @click="sortTable('last30DaysSubmissions')"
                  class="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-black/60 transition-colors select-none"
                >
                  <div class="flex flex-col items-end">
                    <div class="flex items-center gap-2">
                      <span>{{ getDateFilterLabel() }}</span>
                      <div class="flex flex-col">
                        <svg 
                          :class="['w-3 h-3', sortColumn === 'last30DaysSubmissions' && sortDirection === 'asc' ? 'text-blue-400' : 'text-gray-600']"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                        </svg>
                        <svg 
                          :class="['w-3 h-3 -mt-1', sortColumn === 'last30DaysSubmissions' && sortDirection === 'desc' ? 'text-blue-400' : 'text-gray-600']"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    <span class="text-xs font-normal normal-case text-gray-500 mt-0.5">{{ getDateRangeLabel() }}</span>
                  </div>
                </th>
                <th 
                  @click="sortTable('previousMonthSubmissions')"
                  class="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-black/60 transition-colors select-none"
                >
                  <div class="flex flex-col items-end">
                    <div class="flex items-center gap-2">
                      <span>{{ getPreviousPeriodLabel() }}</span>
                      <div class="flex flex-col">
                        <svg 
                          :class="['w-3 h-3', sortColumn === 'previousMonthSubmissions' && sortDirection === 'asc' ? 'text-blue-400' : 'text-gray-600']"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                        </svg>
                        <svg 
                          :class="['w-3 h-3 -mt-1', sortColumn === 'previousMonthSubmissions' && sortDirection === 'desc' ? 'text-blue-400' : 'text-gray-600']"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    <span class="text-xs font-normal normal-case text-gray-500 mt-0.5">{{ getPreviousPeriodSubtitle() }}</span>
                  </div>
                </th>
                <th 
                  @click="sortTable('change')"
                  class="px-6 py-4 text-center text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-black/60 transition-colors select-none"
                >
                  <div class="flex items-center justify-center gap-2">
                    <span>Change</span>
                    <div class="flex flex-col">
                      <svg 
                        :class="['w-3 h-3', sortColumn === 'change' && sortDirection === 'asc' ? 'text-blue-400' : 'text-gray-600']"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                      </svg>
                      <svg 
                        :class="['w-3 h-3 -mt-1', sortColumn === 'change' && sortDirection === 'desc' ? 'text-blue-400' : 'text-gray-600']"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </th>
                <th 
                  @click="sortTable('lastSubmission')"
                  class="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-black/60 transition-colors select-none"
                >
                  <div class="flex items-center gap-2">
                    <span>Last Submission</span>
                    <div class="flex flex-col">
                      <svg 
                        :class="['w-3 h-3', sortColumn === 'lastSubmission' && sortDirection === 'asc' ? 'text-blue-400' : 'text-gray-600']"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                      </svg>
                      <svg 
                        :class="['w-3 h-3 -mt-1', sortColumn === 'lastSubmission' && sortDirection === 'desc' ? 'text-blue-400' : 'text-gray-600']"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/10">
              <tr
                v-for="account in paginatedAccounts"
                :key="`${account.formId}-${dateFilter}`"
                class="hover:bg-white/5 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center gap-3">
                    <router-link
                      v-if="account.accountSlug"
                      :to="`/account/${account.accountSlug}/dashboard`"
                      target="_blank"
                      class="text-blue-400 hover:text-blue-300 transition-colors font-medium inline-flex items-center"
                      title="View Dashboard"
                    >
                      {{ account.accountName || account.formTitle || 'N/A' }}
                      <svg class="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </router-link>
                    <span v-else class="text-white font-medium">
                      {{ account.accountName || account.formTitle || 'N/A' }}
                    </span>
                    <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                          :class="getCachedAccountStatus(account) === 'active' ? 'bg-green-500/20 text-green-300 border border-green-400/30' : 'bg-red-500/20 text-red-300 border border-red-400/30'">
                      <div class="w-2 h-2 rounded-full mr-1.5"
                           :class="getCachedAccountStatus(account) === 'active' ? 'bg-green-400 animate-pulse' : 'bg-red-400'"></div>
                      {{ getCachedAccountStatus(account) === 'active' ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <span class="text-white font-semibold">{{ formatNumber(account.totalSubmissions) }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <span class="text-white font-semibold">{{ formatNumber(getFilteredSubmissions(account)) }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <span class="text-gray-300 font-medium">{{ formatNumber(getPreviousMonthSubmissions(account)) }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span 
                    :class="[
                      'font-semibold text-sm',
                      getMonthChange(account) > 0 ? 'text-green-400' : 
                      getMonthChange(account) < 0 ? 'text-red-400' : 
                      'text-gray-400'
                    ]"
                  >
                    {{ getMonthChangeDisplay(account) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="text-gray-300 text-sm">
                    {{ account.lastSubmission ? formatDate(account.lastSubmission) : 'N/A' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="filteredAccounts.length === 0 && !isLoading" class="p-12 text-center">
          <svg class="w-16 h-16 text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p class="text-gray-400 text-lg mb-2" v-if="searchQuery">No accounts found matching "{{ searchQuery }}"</p>
          <p class="text-gray-400 text-lg mb-2" v-else>No account data available</p>
          <p class="text-gray-500 text-sm mb-4" v-if="!searchQuery">
            This could mean:
          </p>
          <ul class="text-gray-500 text-sm text-left max-w-md mx-auto space-y-1" v-if="!searchQuery">
            <li>• No forms exist in Gravity Forms</li>
            <li>• API credentials need to be configured</li>
            <li>• Check Netlify function logs for details</li>
          </ul>
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-full text-white text-sm font-medium transition-all"
          >
            Clear Search
          </button>
        </div>

        <!-- Pagination -->
        <div v-if="filteredAccounts.length > 0 && totalPages > 1" class="px-6 py-4 border-t border-white/10 flex items-center justify-between">
          <div class="text-sm text-gray-400">
            Showing {{ pageStart }} to {{ pageEnd }} of {{ filteredAccounts.length }} accounts
            <span v-if="searchQuery" class="text-blue-400">(filtered from {{ globalFilteredAccounts.length }} total)</span>
          </div>
          <div class="flex items-center space-x-2">
            <button
              @click="currentPage = Math.max(1, currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-4 py-2 bg-black/40 border border-white/10 rounded-full text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/60"
            >
              Previous
            </button>
            <div class="flex items-center space-x-1" v-if="visiblePages && visiblePages.length > 0">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="currentPage = page"
                :class="[
                  'px-3 py-1 rounded-full text-sm font-medium transition-all',
                  page === currentPage
                    ? 'bg-blue-600 text-white'
                    : 'bg-black/40 border border-white/10 text-gray-300 hover:bg-black/60'
                ]"
              >
                {{ page }}
              </button>
            </div>
            <button
              @click="currentPage = Math.min(totalPages, currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="px-4 py-2 bg-black/40 border border-white/10 rounded-full text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/60"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Report Success Modal -->
    <div
      v-if="showReportSuccessModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="closeReportSuccessModal"
    >
      <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeReportSuccessModal"></div>
      <div class="relative bg-white border border-gray-200 rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div v-if="reportSuccessData && !reportSuccessData.error" class="text-center">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-2xl font-bold text-gray-800 mb-2">
            {{ reportSuccessData.emailSent && reportSuccessData.slackSent ? 'Report Sent to Email & Slack!' : reportSuccessData.emailSent ? 'Report Sent Successfully!' : reportSuccessData.slackSent ? 'Report Sent to Slack!' : 'Report Downloaded Successfully!' }}
          </h3>
          <div class="mt-6 space-y-3">
            <div class="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
              <span class="text-gray-600 text-sm font-medium">Period</span>
              <span class="text-gray-800 font-semibold">{{ reportSuccessData.period }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
              <span class="text-gray-600 text-sm font-medium">Accounts</span>
              <span class="text-gray-800 font-semibold">{{ formatNumber(reportSuccessData.accounts) }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
              <span class="text-gray-600 text-sm font-medium">Submissions</span>
              <span class="text-gray-800 font-semibold">{{ formatNumber(reportSuccessData.submissions) }}</span>
            </div>
            <div v-if="reportSuccessData.emailSent" class="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
              <span class="text-gray-600 text-sm font-medium">Sent to</span>
              <span class="text-gray-800 font-semibold">{{ reportSuccessData.recipient }}</span>
            </div>
            <div v-if="reportSuccessData.slackSent" class="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
              <span class="text-gray-600 text-sm font-medium">Sent to</span>
              <span class="text-gray-800 font-semibold">Slack Channel</span>
            </div>
          </div>
          <button
            @click="closeReportSuccessModal"
            class="mt-6 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors"
          >
            Close
          </button>
        </div>
        <div v-else-if="reportSuccessData && reportSuccessData.error" class="text-center">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-2xl font-bold text-gray-800 mb-2">Error</h3>
          <p class="text-gray-600 mb-6">{{ reportSuccessData.message }}</p>
          <button
            @click="closeReportSuccessModal"
            class="w-full px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    
    <!-- Account Details Modal -->
    <div
      v-if="showAccountModal && selectedAccount"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      @click.self="closeAccountModal"
    >
      <div class="bg-white border border-gray-200 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 bg-white">
          <div>
            <h3 class="text-xl font-bold text-gray-900 mb-1">Account Details</h3>
            <p class="text-gray-600 text-xs font-medium">{{ selectedAccount.accountName || selectedAccount.formTitle || `Form ${selectedAccount.formId}` }}</p>
          </div>
          <button
            @click="closeAccountModal"
            class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            title="Close"
          >
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div class="p-6 space-y-6">
          <!-- Account Information -->
          <div class="bg-gray-50 border border-gray-200 rounded-2xl p-4">
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-sm font-semibold text-gray-900">Account Information</h4>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div class="bg-white rounded-lg p-4 border border-gray-200">
                <div class="text-gray-600 text-xs font-medium mb-1">Account Name</div>
                <div class="text-gray-900 font-medium">{{ selectedAccount.accountName || selectedAccount.formTitle || 'N/A' }}</div>
              </div>
              <div class="bg-white rounded-lg p-4 border border-gray-200">
                <div class="text-gray-600 text-xs font-medium mb-1">Form ID</div>
                <div class="text-gray-900 font-medium">{{ selectedAccount.formId }}</div>
              </div>
              <div class="bg-white rounded-lg p-4 border border-gray-200">
                <div class="text-gray-600 text-xs font-medium mb-1">Account Slug</div>
                <div class="text-gray-900 font-medium">{{ selectedAccount.accountSlug || 'Not configured' }}</div>
              </div>
              <div class="bg-white rounded-lg p-4 border border-gray-200">
                <div class="text-gray-600 text-xs font-medium mb-3">Status</div>
                <div class="flex flex-col gap-3">
                  <span
                    class="inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold w-fit"
                    :class="getAccountStatus(selectedAccount) === 'active' ? 'bg-green-100 text-green-700 border-2 border-green-400' : 'bg-red-100 text-red-700 border-2 border-red-400'"
                  >
                    {{ getAccountStatus(selectedAccount) === 'active' ? 'Active' : 'Inactive' }}
                  </span>
                  <button
                    v-if="isAdmin"
                    @click="openConfirmModal"
                    :disabled="isTogglingFormStatus"
                    class="inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-md"
                    :class="getAccountStatus(selectedAccount) === 'active' 
                      ? 'bg-red-600 text-white hover:bg-red-700 border-2 border-red-700' 
                      : 'bg-green-600 text-white hover:bg-green-700 border-2 border-green-700'"
                    :title="getAccountStatus(selectedAccount) === 'active' ? 'Deactivate form in Gravity Forms' : 'Activate form in Gravity Forms'"
                  >
                    <svg v-if="isTogglingFormStatus" class="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <svg v-else class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path v-if="getAccountStatus(selectedAccount) === 'active'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {{ getAccountStatus(selectedAccount) === 'active' ? 'Deactivate Account' : 'Activate Account' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Submission Statistics -->
          <div class="bg-gray-50 border border-gray-200 rounded-2xl p-4">
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-sm font-semibold text-gray-900">Submission Statistics</h4>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div class="bg-white rounded-lg p-4 border border-gray-200">
                <div class="text-gray-600 text-xs font-medium mb-1">Total Submissions</div>
                <div class="text-gray-900 text-xl font-bold">{{ formatNumber(selectedAccount.totalSubmissions || 0) }}</div>
              </div>
              <div class="bg-white rounded-lg p-4 border border-gray-200">
                <div class="text-gray-600 text-xs font-medium mb-1">{{ getDateFilterLabel() }}</div>
                <div class="text-gray-900 text-xl font-bold">{{ formatNumber(getFilteredSubmissions(selectedAccount)) }}</div>
              </div>
              <div class="bg-white rounded-lg p-4 border border-gray-200">
                <div class="text-gray-600 text-xs font-medium mb-1">Last 30 Days</div>
                <div class="text-gray-900 text-xl font-bold">{{ formatNumber(selectedAccount.last30DaysSubmissions || 0) }}</div>
              </div>
              <div class="bg-white rounded-lg p-4 border border-gray-200">
                <div class="text-gray-600 text-xs font-medium mb-1">Last Submission</div>
                <div class="text-gray-900 font-medium">{{ selectedAccount.lastSubmission ? formatDate(selectedAccount.lastSubmission) : 'Never' }}</div>
              </div>
            </div>
          </div>

          <!-- Quick Actions -->
          <div>
            <h4 class="text-sm font-semibold text-gray-900 mb-4">Quick Actions</h4>
            <div class="flex flex-wrap items-center gap-3">
              <router-link
                v-if="selectedAccount.accountSlug"
                :to="`/account/${selectedAccount.accountSlug}/dashboard`"
                class="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-full text-white text-sm font-medium transition-all flex items-center whitespace-nowrap"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Dashboard
              </router-link>
              <button
                class="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-full text-white text-sm font-medium transition-all flex items-center whitespace-nowrap"
                @click="fetchStats(true)"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Data
              </button>
            </div>
          </div>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-white">
          <button
            @click="closeAccountModal"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal for Activate/Deactivate -->
    <div
      v-if="showConfirmModal"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      @click.self="!isTogglingFormStatus && closeConfirmModal()"
    >
      <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="!isTogglingFormStatus && closeConfirmModal()"></div>
      <div class="relative bg-white border border-gray-200 rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div class="text-center">
          <div v-if="isTogglingFormStatus" class="mb-4">
            <div class="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          </div>
          <h3 class="text-2xl font-bold text-gray-900 mb-2">Confirm Action</h3>
          <p class="text-gray-600 mb-6">
            <span v-if="isTogglingFormStatus">Processing your request...</span>
            <span v-else>Are you sure you want to {{ confirmModalAction }} this account?</span>
          </p>
          <div class="flex items-center gap-3">
            <button
              @click="closeConfirmModal"
              :disabled="isTogglingFormStatus"
              class="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed rounded-lg text-gray-700 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              @click="toggleFormStatus"
              :disabled="isTogglingFormStatus"
              class="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors flex items-center justify-center"
            >
              <svg v-if="isTogglingFormStatus" class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isTogglingFormStatus ? 'Processing...' : 'OK' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Notification Modal -->
    <div
      v-if="showStatusSuccessModal"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      @click.self="closeStatusSuccessModal"
    >
      <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeStatusSuccessModal"></div>
      <div class="relative bg-white border border-gray-200 rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div class="text-center">
          <div 
            class="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            :class="statusSuccessIsError ? 'bg-red-100' : 'bg-green-100'"
          >
            <svg 
              v-if="!statusSuccessIsError"
              class="w-8 h-8 text-green-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <svg 
              v-else
              class="w-8 h-8 text-red-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 
            class="text-2xl font-bold mb-2"
            :class="statusSuccessIsError ? 'text-red-900' : 'text-gray-900'"
          >
            {{ statusSuccessIsError ? 'Error' : 'Success' }}
          </h3>
          <p 
            class="mb-6"
            :class="statusSuccessIsError ? 'text-red-700' : 'text-gray-600'"
          >
            {{ statusSuccessMessage }}
          </p>
          <button
            @click="closeStatusSuccessModal"
            class="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    
    <!-- Email Preview Modal -->
    <div
      v-if="showEmailPreview"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      @click.self="closeEmailPreview"
    >
      <div class="bg-white rounded-3xl shadow-2xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white rounded-t-3xl">
          <div>
            <h3 class="text-xl font-bold text-gray-900">Report Settings</h3>
            <p class="text-gray-600 text-sm mt-1">Preview, configure, and schedule email reports</p>
          </div>
          <button
            @click="closeEmailPreview"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- Tabs -->
        <div class="px-6 pt-4 border-b border-gray-200 bg-white">
          <div class="flex space-x-1">
            <button
              @click="emailPreviewTab = 'preview'"
              :class="emailPreviewTab === 'preview' 
                ? 'border-b-2 border-blue-600 text-blue-600' 
                : 'text-gray-600 hover:text-gray-900'"
              class="px-4 py-2 font-medium text-sm transition-colors"
            >
              Preview
            </button>
            <button
              @click="emailPreviewTab = 'settings'"
              :class="emailPreviewTab === 'settings' 
                ? 'border-b-2 border-blue-600 text-blue-600' 
                : 'text-gray-600 hover:text-gray-900'"
              class="px-4 py-2 font-medium text-sm transition-colors"
            >
              Email Settings
            </button>
            <button
              @click="emailPreviewTab = 'scheduler'"
              :class="emailPreviewTab === 'scheduler' 
                ? 'border-b-2 border-blue-600 text-blue-600' 
                : 'text-gray-600 hover:text-gray-900'"
              class="px-4 py-2 font-medium text-sm transition-colors"
            >
              Report Scheduler
            </button>
            <button
              @click="emailPreviewTab = 'history'"
              :class="emailPreviewTab === 'history' 
                ? 'border-b-2 border-blue-600 text-blue-600' 
                : 'text-gray-600 hover:text-gray-900'"
              class="px-4 py-2 font-medium text-sm transition-colors"
            >
              Sent Reports
            </button>
          </div>
        </div>
        
        <!-- Tab Content -->
        <div class="flex-1 overflow-auto bg-gray-50">
          <!-- Preview Tab -->
          <div v-if="emailPreviewTab === 'preview'" class="p-6">
            <div v-if="emailPreviewContent" class="bg-gray-100 rounded-lg p-4">
              <div 
                v-html="emailPreviewContent" 
                class="bg-white rounded-lg shadow-lg mx-auto"
                style="max-width: 100%; overflow-x: auto;"
              ></div>
            </div>
            <div v-else class="flex items-center justify-center h-64">
              <div class="text-center">
                <svg class="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <p class="text-gray-600">Generating preview...</p>
              </div>
            </div>
          </div>
          
          <!-- Email Settings Tab -->
          <div v-if="emailPreviewTab === 'settings'" class="p-6">
            <div class="bg-white rounded-lg border border-gray-200 p-6">
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-gray-900 font-semibold text-base">Email Configuration</h4>
                <div class="flex items-center gap-2">
                  <span v-if="showSaveSuccess" class="text-green-600 text-xs font-medium flex items-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Saved!
                  </span>
                  <span v-else class="text-gray-500 text-xs">Settings saved automatically</span>
                </div>
              </div>
              <div class="space-y-4">
                <!-- Recipients -->
                <div>
                  <label class="block text-gray-700 text-sm font-medium mb-2">Recipients (comma-separated)</label>
                  <input
                    v-model="emailSettings.recipients"
                    type="text"
                    placeholder="email1@example.com, email2@example.com"
                    class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p class="text-gray-500 text-xs mt-1.5">Separate multiple emails with commas</p>
                </div>
                
                <!-- From Email -->
                <div>
                  <label class="block text-gray-700 text-sm font-medium mb-2">From Email</label>
                  <input
                    v-model="emailSettings.fromEmail"
                    type="email"
                    placeholder="noreply@example.com"
                    class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <!-- Subject Prefix -->
                <div>
                  <label class="block text-gray-700 text-sm font-medium mb-2">Subject Prefix</label>
                  <input
                    v-model="emailSettings.subjectPrefix"
                    type="text"
                    placeholder="Engagr Report"
                    class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p class="text-gray-500 text-xs mt-1.5">Subject will be: "<span class="font-medium text-gray-900">{{ emailSettings.subjectPrefix }} - {{ getDateFilterLabel() }}</span>"</p>
                </div>
              </div>
              
              <!-- Action Buttons -->
              <div class="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  @click="saveEmailSettings"
                  :disabled="isSavingSettings"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-if="!isSavingSettings">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <svg class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {{ isSavingSettings ? 'Saving...' : 'Save Settings' }}
                </button>
                <button
                  @click="sendTestReport"
                  :disabled="isSendingTestReport || !emailSettings.recipients || emailSettings.recipients.trim() === ''"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-if="!isSendingTestReport">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <svg class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {{ isSendingTestReport ? 'Sending...' : 'Send Test Report' }}
                </button>
              </div>
            </div>
          </div>
          
          <!-- Report Scheduler Tab -->
          <div v-if="emailPreviewTab === 'scheduler'" class="p-6">
            <div class="bg-white rounded-lg border border-gray-200 p-6">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h4 class="text-gray-900 font-semibold text-base">Schedule Automated Reports</h4>
                  <div class="flex items-center gap-2 mt-1">
                    <span v-if="showSaveSuccess" class="text-green-600 text-xs font-medium flex items-center">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Saved!
                    </span>
                    <span v-else class="text-gray-500 text-xs">Settings saved automatically</span>
                  </div>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="emailSettings.schedulerEnabled"
                    class="sr-only peer"
                  />
                  <div class="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span class="ml-3 text-sm font-medium text-gray-700">
                    {{ emailSettings.schedulerEnabled ? 'Enabled' : 'Disabled' }}
                  </span>
                </label>
              </div>
              
              <div v-if="emailSettings.schedulerEnabled" class="space-y-4">
                <!-- Frequency -->
                <div>
                  <label class="block text-gray-700 text-sm font-medium mb-2">Frequency</label>
                  <select
                    v-model="emailSettings.frequency"
                    class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                
                <!-- Day of Month (for monthly) -->
                <div v-if="emailSettings.frequency === 'monthly'">
                  <label class="block text-gray-700 text-sm font-medium mb-2">Day of Month</label>
                  <select
                    v-model.number="emailSettings.dayOfMonth"
                    class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option v-for="day in 31" :key="day" :value="day">{{ day }}</option>
                  </select>
                  <p class="text-gray-500 text-xs mt-1.5">Report will be sent on the {{ getOrdinal(emailSettings.dayOfMonth || 1) }} of each month</p>
                </div>
                
                <!-- Day of Week (for weekly) -->
                <div v-if="emailSettings.frequency === 'weekly'">
                  <label class="block text-gray-700 text-sm font-medium mb-2">Day of Week</label>
                  <select
                    v-model.number="emailSettings.dayOfWeek"
                    class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option :value="0">Sunday</option>
                    <option :value="1">Monday</option>
                    <option :value="2">Tuesday</option>
                    <option :value="3">Wednesday</option>
                    <option :value="4">Thursday</option>
                    <option :value="5">Friday</option>
                    <option :value="6">Saturday</option>
                  </select>
                </div>
                
                <!-- Time Settings -->
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-gray-700 text-sm font-medium mb-2">Hour (24-hour)</label>
                    <select
                      v-model.number="emailSettings.hour"
                      class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option v-for="h in 24" :key="h - 1" :value="h - 1">
                        {{ String(h - 1).padStart(2, '0') }}:00
                      </option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-gray-700 text-sm font-medium mb-2">Minute</label>
                    <select
                      v-model.number="emailSettings.minute"
                      class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option :value="0">:00</option>
                      <option :value="15">:15</option>
                      <option :value="30">:30</option>
                      <option :value="45">:45</option>
                    </select>
                  </div>
                </div>
                
                <!-- Reports On -->
                <div>
                  <label class="block text-gray-700 text-sm font-medium mb-2">Reports On</label>
                  <select
                    v-model="emailSettings.reportsOn"
                    class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="previousMonth">Previous Month's Data</option>
                    <option value="currentMonth">Current Month's Data</option>
                  </select>
                  <p class="text-gray-500 text-xs mt-1.5">
                    {{ emailSettings.reportsOn === 'previousMonth' 
                      ? 'Report will include data from the previous month' 
                      : 'Report will include data from the current month' }}
                  </p>
                </div>
                
                <!-- Slack Integration -->
                <div class="pt-3 border-t border-gray-200">
                  <div class="flex items-center justify-between mb-4">
                    <div>
                      <h4 class="text-gray-900 font-semibold text-sm mb-1">Slack Notifications</h4>
                      <p class="text-gray-500 text-xs">Send report summary to Slack channel</p>
                    </div>
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        v-model="emailSettings.slackEnabled"
                        class="sr-only peer"
                      />
                      <div class="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      <span class="ml-3 text-sm font-medium text-gray-700">
                        {{ emailSettings.slackEnabled ? 'Enabled' : 'Disabled' }}
                      </span>
                    </label>
                  </div>
                  
                  <div v-if="emailSettings.slackEnabled" class="mt-4 space-y-3">
                    <div>
                      <label class="block text-gray-700 text-sm font-medium mb-2">
                        Slack Webhook URL (Optional)
                      </label>
                      <input
                        v-model="emailSettings.slackWebhookUrl"
                        type="text"
                        placeholder="Leave empty to use environment variable"
                        class="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <!-- Current Webhook Status -->
                      <div v-if="emailSettings.slackWebhookUrl && emailSettings.slackWebhookUrl.trim() !== ''" class="mt-2 flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <svg class="w-4 h-4 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div class="flex-1 min-w-0">
                          <p class="text-xs font-medium text-indigo-900">Using Custom Webhook</p>
                          <p class="text-xs text-indigo-700 truncate" :title="emailSettings.slackWebhookUrl">{{ emailSettings.slackWebhookUrl }}</p>
                        </div>
                      </div>
                      <div v-else class="mt-2 flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                        <svg class="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div class="flex-1 min-w-0">
                          <p class="text-xs font-medium text-gray-700">Using Environment Variable</p>
                          <p class="text-xs text-gray-600 truncate" :title="environmentSlackWebhookUrl || 'Not configured'">
                            {{ environmentSlackWebhookUrl || 'SLACK_WEBHOOK_URL not configured' }}
                          </p>
                        </div>
                      </div>
                      <p class="text-gray-500 text-xs mt-1.5">
                        If left empty, the system will use SLACK_WEBHOOK_URL from environment variables
                      </p>
                    </div>
                    <div class="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                      <div class="flex items-start">
                        <svg class="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div class="text-xs text-indigo-800">
                          <p class="font-medium mb-1">When enabled:</p>
                          <ul class="list-disc list-inside space-y-0.5">
                            <li>Email report will be sent to configured recipients</li>
                            <li>Slack message with report summary will be sent to your Slack channel</li>
                            <li>Both will be sent at the scheduled time</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Next Run Display -->
                <div class="pt-3 border-t border-gray-200">
                  <div class="flex items-center justify-between">
                    <span class="text-gray-600 text-sm">Next Scheduled Run</span>
                    <span class="text-gray-900 font-medium text-sm">{{ calculateNextRun() }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-8 text-gray-500 text-sm">
                Enable scheduling to configure automated reports
              </div>
              
              <!-- Action Buttons -->
              <div class="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  @click="saveEmailSettings"
                  :disabled="isSavingSettings"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-if="!isSavingSettings">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <svg class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {{ isSavingSettings ? 'Saving...' : 'Save Settings' }}
                </button>
                <button
                  @click="sendTestReport"
                  :disabled="isSendingTestReport || !emailSettings.recipients || emailSettings.recipients.trim() === ''"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-if="!isSendingTestReport">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <svg class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {{ isSendingTestReport ? 'Sending...' : 'Send Test Report' }}
                </button>
              </div>
            </div>
          </div>
          
          <!-- Sent Reports History Tab -->
          <div v-if="emailPreviewTab === 'history'" class="p-6">
            <div class="bg-white rounded-lg border border-gray-200 p-6">
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-gray-900 font-semibold text-base">Sent Reports History</h4>
                <button
                  @click="clearSentReportsHistory"
                  v-if="sentReportsHistory.length > 0"
                  class="px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                >
                  Clear History
                </button>
              </div>
              
              <div v-if="sentReportsHistory.length === 0" class="text-center py-12">
                <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p class="text-gray-500 text-sm">No reports sent yet</p>
                <p class="text-gray-400 text-xs mt-1">Sent reports will appear here</p>
              </div>
              
              <div v-else class="space-y-3">
                <div
                  v-for="(report, index) in sentReportsHistory"
                  :key="index"
                  class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-2">
                        <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span class="font-medium text-gray-900">{{ report.subject }}</span>
                      </div>
                      <div class="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span class="font-medium">Recipients:</span>
                          <span class="ml-2">{{ report.recipients.join(', ') }}</span>
                        </div>
                        <div>
                          <span class="font-medium">Period:</span>
                          <span class="ml-2">{{ report.period }}</span>
                        </div>
                        <div>
                          <span class="font-medium">Accounts:</span>
                          <span class="ml-2">{{ report.accounts }}</span>
                        </div>
                        <div>
                          <span class="font-medium">Submissions:</span>
                          <span class="ml-2">{{ formatNumber(report.submissions) }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-xs text-gray-500 mb-1">{{ formatReportDate(report.sentAt) }}</div>
                      <div class="text-xs text-gray-400">{{ formatReportTime(report.sentAt) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Modal Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-white rounded-b-3xl">
          <button
            @click="closeEmailPreview"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 text-sm font-medium transition-all"
          >
            Cancel
          </button>
          <button
            @click="sendEmailFromPreview"
            :disabled="isSendingEmail || !emailSettings.recipients || emailSettings.recipients.trim() === ''"
            class="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm font-medium transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-if="!isSendingEmail">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <svg class="w-4 h-4 mr-2 animate-spin flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {{ isSendingEmail ? 'Sending...' : 'Send Email' }}
          </button>
        </div>
      </div>
    </div>

    <!-- User Management Modal - White Popup Style -->
    <div v-if="showUserManagement" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="showUserManagement = false"></div>
      <div class="relative bg-white border border-gray-200 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="flex justify-between items-center p-6 pb-4 border-b border-gray-200">
          <h2 class="text-2xl font-bold text-gray-800">User Management</h2>
          <button
            @click="showUserManagement = false"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <!-- Tabs -->
          <div class="flex gap-2 mb-6 border-b border-gray-200">
            <button
              @click="userManagementTab = 'list'"
              :class="[
                'px-4 py-2 font-medium transition-colors border-b-2',
                userManagementTab === 'list'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-600 border-transparent hover:text-gray-800'
              ]"
            >
              Users List
            </button>
            <button
              @click="userManagementTab = 'add'"
              :class="[
                'px-4 py-2 font-medium transition-colors border-b-2',
                userManagementTab === 'add'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-600 border-transparent hover:text-gray-800'
              ]"
            >
              Add User
            </button>
            <button
              @click="userManagementTab = 'reset'"
              :class="[
                'px-4 py-2 font-medium transition-colors border-b-2',
                userManagementTab === 'reset'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-600 border-transparent hover:text-gray-800'
              ]"
            >
              Reset Password
            </button>
            <button
              @click="userManagementTab = 'change-password'"
              :class="[
                'px-4 py-2 font-medium transition-colors border-b-2',
                userManagementTab === 'change-password'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-600 border-transparent hover:text-gray-800'
              ]"
            >
              Change My Password
            </button>
            <button
              v-if="editingUser"
              @click="userManagementTab = 'edit'"
              :class="[
                'px-4 py-2 font-medium transition-colors border-b-2',
                userManagementTab === 'edit'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-600 border-transparent hover:text-gray-800'
              ]"
            >
              Edit User
            </button>
          </div>

          <!-- Users List Tab -->
          <div v-if="userManagementTab === 'list'" class="space-y-4">
            <div v-if="isLoadingUsers" class="text-center py-8 text-gray-600">
              Loading users...
            </div>
            <div v-else-if="users.length === 0" class="text-center py-8 text-gray-600">
              No users found
            </div>
            <div v-else class="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="user in users" :key="user.id" class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{{ user.name || 'N/A' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{{ user.email }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm">
                      <span :class="{
                        'px-2 py-1 text-xs font-semibold rounded-full': true,
                        'bg-blue-100 text-blue-800': user.role === 'super_admin',
                        'bg-blue-100 text-blue-800': user.role === 'admin',
                        'bg-gray-100 text-gray-800': user.role === 'user' || !user.role
                      }">
                        {{ user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'User' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {{ user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        @click="editUser(user)"
                        class="text-blue-600 hover:text-blue-800 mr-4"
                        :disabled="!isAdmin || (user.role === 'super_admin' && !isSuperAdmin)"
                        :class="{ 'opacity-50 cursor-not-allowed': !isAdmin || (user.role === 'super_admin' && !isSuperAdmin) }"
                        :title="user.role === 'super_admin' && !isSuperAdmin ? 'Super admins can only be edited by other super admins' : !isAdmin ? 'Only admins and super admins can edit users' : ''"
                      >
                        Edit
                      </button>
                      <button
                        @click="confirmDeleteUser(user)"
                        class="text-red-600 hover:text-red-800"
                        :disabled="currentUser?.email === user.email || !isAdmin || user.role === 'super_admin'"
                        :class="{ 'opacity-50 cursor-not-allowed': currentUser?.email === user.email || !isAdmin || user.role === 'super_admin' }"
                        :title="user.role === 'super_admin' ? 'Super admins cannot be deleted' : currentUser?.email === user.email ? 'Cannot delete your own account' : !isAdmin ? 'Only admins and super admins can delete users' : ''"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Add User Tab -->
          <div v-if="userManagementTab === 'add'" class="space-y-4">
            <form @submit.prevent="createUser" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  v-model="newUserEmail"
                  type="email"
                  required
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  v-model="newUserPassword"
                  type="password"
                  required
                  minlength="8"
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                  placeholder="Secure password (min 8 characters)"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Name (Optional)</label>
                <input
                  v-model="newUserName"
                  type="text"
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                  placeholder="User's full name"
                />
              </div>

              <div v-if="isAdmin">
                <label class="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  v-model="newUserRole"
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin" :disabled="!isSuperAdmin">Super Admin (super admin only)</option>
                </select>
                <p class="mt-1 text-xs text-gray-500">
                  {{ isSuperAdmin ? 'You can set any role' : 'Admins can set user and admin roles. Super admin role requires super admin access.' }}
                </p>
              </div>

              <div v-if="userCreationError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {{ userCreationError }}
              </div>

              <div v-if="userCreationSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                User created successfully!
              </div>

              <div class="flex gap-3 pt-4">
                <button
                  type="button"
                  @click="showUserManagement = false"
                  class="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="isCreatingUser || !newUserEmail || !newUserPassword"
                  class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                >
                  <span v-if="isCreatingUser">Creating...</span>
                  <span v-else>Create User</span>
                </button>
              </div>
            </form>
          </div>

          <!-- Reset Password Tab -->
          <div v-if="userManagementTab === 'reset'" class="space-y-4">
            <form @submit.prevent="handleForgotPassword" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  v-model="resetEmail"
                  type="email"
                  required
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                  placeholder="user@example.com"
                />
                <p class="mt-2 text-sm text-gray-600">
                  Enter the email address associated with the account. A reset token will be generated.
                </p>
              </div>

              <div v-if="resetToken" class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p class="text-sm font-medium text-blue-800 mb-2">Reset Token Generated:</p>
                <code class="block p-2 bg-white border border-blue-200 rounded text-xs text-gray-800 break-all">{{ resetToken }}</code>
                <p class="mt-2 text-xs text-blue-700">
                  ⚠️ In production, this token would be sent via email. For now, copy it to use in the reset form below.
                </p>
              </div>

              <div v-if="resetToken" class="pt-4 border-t border-gray-200">
                <h3 class="text-lg font-semibold text-gray-800 mb-4">Reset Password</h3>
                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Reset Token</label>
                    <input
                      v-model="resetPasswordToken"
                      type="text"
                      required
                      class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                      placeholder="Paste reset token here"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <input
                      v-model="resetNewPassword"
                      type="password"
                      required
                      minlength="8"
                      class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                      placeholder="New password (min 8 characters)"
                    />
                  </div>
                  <button
                    type="button"
                    @click="handleResetPassword"
                    :disabled="isResettingPassword || !resetPasswordToken || !resetNewPassword"
                    class="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                  >
                    <span v-if="isResettingPassword">Resetting...</span>
                    <span v-else>Reset Password</span>
                  </button>
                </div>
              </div>

              <div v-if="resetPasswordError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {{ resetPasswordError }}
              </div>

              <div v-if="resetPasswordSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                Password reset successfully!
              </div>

              <div v-if="!resetToken" class="flex gap-3 pt-4">
                <button
                  type="button"
                  @click="showUserManagement = false"
                  class="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="isRequestingReset || !resetEmail"
                  class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                >
                  <span v-if="isRequestingReset">Generating...</span>
                  <span v-else>Generate Reset Token</span>
                </button>
              </div>
            </form>
          </div>

          <!-- Change Password Tab -->
          <div v-if="userManagementTab === 'change-password'" class="space-y-4">
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p class="text-sm text-blue-800">
                <strong>Change your password:</strong> Enter your current password and choose a new secure password.
              </p>
            </div>
            <form @submit.prevent="handleChangePassword" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                <input
                  v-model="changePasswordCurrent"
                  type="password"
                  required
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                  placeholder="Enter your current password"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input
                  v-model="changePasswordNew"
                  type="password"
                  required
                  minlength="8"
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                  placeholder="New password (min 8 characters)"
                />
                <p class="mt-1 text-xs text-gray-500">Password must be at least 8 characters long</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <input
                  v-model="changePasswordConfirm"
                  type="password"
                  required
                  minlength="8"
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                  placeholder="Confirm new password"
                />
              </div>
              <button
                type="submit"
                :disabled="isChangingPassword || !changePasswordCurrent || !changePasswordNew || !changePasswordConfirm || changePasswordNew !== changePasswordConfirm"
                class="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
              >
                <span v-if="isChangingPassword">Changing Password...</span>
                <span v-else>Change Password</span>
              </button>
            </form>

            <div v-if="changePasswordError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {{ changePasswordError }}
            </div>
            <div v-if="changePasswordSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
              {{ changePasswordSuccess }}
            </div>
          </div>

          <!-- Edit User Tab -->
          <div v-if="userManagementTab === 'edit' && editingUser" class="space-y-4">
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p class="text-sm text-blue-800">
                <strong>Edit User:</strong> Update user information and role. {{ isSuperAdmin ? 'As a super admin, you can change any role including super admin.' : 'You can change user and admin roles.' }}
              </p>
            </div>
            <form @submit.prevent="saveUser" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  v-model="editingUser.email"
                  type="email"
                  required
                  disabled
                  class="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 cursor-not-allowed"
                  placeholder="user@example.com"
                />
                <p class="mt-1 text-xs text-gray-500">Email cannot be changed</p>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  v-model="editingUser.name"
                  type="text"
                  required
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                  placeholder="User's full name"
                />
              </div>

              <div v-if="isAdmin">
                <label class="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  v-model="editingUserRole"
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  :disabled="editingUser.email === currentUser?.email"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin" :disabled="!isSuperAdmin">Super Admin {{ isSuperAdmin ? '' : '(super admin only)' }}</option>
                </select>
                <p class="mt-1 text-xs text-gray-500">
                  {{ editingUser.email === currentUser?.email ? 'You cannot change your own role' : (isSuperAdmin ? 'You can set any role' : 'Admins can set user and admin roles. Super admin role requires super admin access.') }}
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">New Password (Optional)</label>
                <input
                  v-model="editingUserPassword"
                  type="password"
                  minlength="8"
                  class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                  placeholder="Leave blank to keep current password (min 8 characters if changing)"
                />
                <p class="mt-1 text-xs text-gray-500">Leave blank to keep the current password</p>
              </div>

              <div v-if="userUpdateError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {{ userUpdateError }}
              </div>
              <div v-if="userUpdateSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                User updated successfully!
              </div>

              <div class="flex gap-3 pt-4">
                <button
                  type="button"
                  @click="cancelEdit"
                  class="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="isUpdatingUser || !editingUser.name"
                  class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                >
                  <span v-if="isUpdatingUser">Saving...</span>
                  <span v-else>Save Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="!isAuthenticated && !isCheckingAuth" class="fixed inset-0 flex items-center justify-center z-50">
    <BetterAuthLogin @authenticated="handleAuthenticated" />
  </div>
  <div v-else-if="isCheckingAuth" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="text-white">Loading...</div>
  </div>
</template>

<script>
import axios from 'axios';
import BetterAuthLogin from '@/components/BetterAuthLogin.vue';
import EngagementChart from '@/components/EngagementChart.vue';
import AccountListCard from '@/components/AccountListCard.vue';
import AccountHealthCard from '@/components/AccountHealthCard.vue';
import { auth } from '@/utils/betterAuth';

export default {
  name: 'AdminDashboard',
  components: {
    BetterAuthLogin,
    EngagementChart,
    AccountListCard,
    AccountHealthCard
  },
  data() {
    return {
      isAuthenticated: false,
      isCheckingAuth: true,
      isLoading: true,
      isSyncing: false, // Background sync flag
      error: null,
      stats: null,
      currentPage: 1,
      itemsPerPage: 25,
      searchQuery: '',
      managementSearchQuery: '', // Search query for Account Management section
      statusFilter: 'all', // 'all', 'active', 'inactive' - filters both management section and table
      loadingMessage: 'Loading dashboard data...',
      loadingSubMessage: 'Fetching forms and statistics from Gravity Forms',
      loadingStartTime: null,
      hasLoadedCache: false, // Track if we've loaded cache
      isRefreshing: false, // Track if we're refreshing data
      adminPostId: null, // WordPress post ID for admin page (fetched from slug)
      sortColumn: 'last30DaysSubmissions', // Default sort column
      sortDirection: 'desc', // 'asc' or 'desc'
      dateFilter: 'last30', // 'all', 'last7', 'last30', 'thisMonth', 'lastMonth', 'last12Months'
      isGeneratingReport: false, // Flag for report generation
      isSendingEmail: false, // Flag for email sending
      isSendingSlack: false, // Flag for Slack sending
      showReportSuccessModal: false, // Show success modal after download
      reportSuccessData: null, // Data to show in success modal
      showEmailPreview: false, // Show email preview modal
      emailPreviewContent: null, // HTML content for email preview
      emailPreviewStats: null, // Stats data for email preview
      emailPreviewTab: 'preview', // Current tab: 'preview', 'settings', 'scheduler'
      emailSettings: {
        recipients: 'robert@adme.net.au',
        fromEmail: 'noreply@engagr.driveagent.io',
        subjectPrefix: 'Engagr Report',
        schedulerEnabled: false,
        frequency: 'monthly',
        dayOfMonth: 1,
        dayOfWeek: 1,
        hour: 9,
        minute: 0,
        reportsOn: 'previousMonth',
        slackEnabled: false,
        slackWebhookUrl: ''
      },
      sentReportsHistory: [], // History of sent reports
      settingsSaveTimeout: null, // Timeout for debouncing settings save
      environmentSlackWebhookUrl: null, // Environment webhook URL from backend
      isSavingSettings: false, // Loading state for save button
      isSendingTestReport: false, // Loading state for test report
      showSaveSuccess: false, // Show save success message
      showAccountManagement: false, // Show/hide account management section
      selectedAccount: null, // Account selected for editing/viewing
      showAccountModal: false, // Show account details/edit modal
      showConfirmModal: false, // Show confirmation modal for activate/deactivate
      confirmModalAction: null, // 'activate' or 'deactivate'
      showStatusSuccessModal: false, // Show success notification after status change
      statusSuccessMessage: '', // Success message to display
      statusSuccessIsError: false, // Whether the status message is an error
      showUserManagement: false, // Show user management modal
      userManagementTab: 'list', // 'list', 'add', 'reset'
      users: [], // List of all users
      isLoadingUsers: false,
      currentUser: null, // Currently logged in user
      editingUser: null, // User being edited
      editingUserRole: 'user', // Role for user being edited
      newUserEmail: '',
      newUserPassword: '',
      newUserName: '',
      newUserRole: 'user', // Default role for new users
      isCreatingUser: false,
      userCreationError: null,
      userCreationSuccess: false,
      resetEmail: '',
      resetToken: null,
      resetPasswordToken: '',
      resetNewPassword: '',
      isRequestingReset: false,
      isResettingPassword: false,
      resetPasswordError: null,
      resetPasswordSuccess: false,
      // Change password fields
      changePasswordCurrent: '',
      changePasswordNew: '',
      changePasswordConfirm: '',
      changePasswordError: null,
      changePasswordSuccess: false,
      isChangingPassword: false,
      isTogglingFormStatus: false // Loading state for form status toggle
    };
  },
  watch: {
    searchQuery() {
      // Reset to first page when search changes
      this.currentPage = 1;
    },
    dateFilter() {
      // Reset to first page when date filter changes
      this.currentPage = 1;
    },
    sortColumn() {
      // Reset to first page when sort changes
      this.currentPage = 1;
    },
    sortDirection() {
      // Reset to first page when sort direction changes
      this.currentPage = 1;
    },
    // Auto-save email settings when they change (debounced)
    emailSettings: {
      handler() {
        // Clear existing timeout
        if (this.settingsSaveTimeout) {
          clearTimeout(this.settingsSaveTimeout);
        }
        
        // Debounce save by 500ms to avoid excessive saves
        this.settingsSaveTimeout = setTimeout(() => {
          this.saveEmailSettings();
        }, 500);
      },
      deep: true // Watch nested properties
    },
    isAuthenticated(isAuthenticated) {
      // When user authenticates, load the stats
      if (isAuthenticated) {
        this.fetchStats();
      }
    },
    showUserManagement(newVal) {
      if (newVal && this.userManagementTab === 'list') {
        this.loadUsers();
      }
    },
    userManagementTab(newVal) {
      if (newVal === 'list' && this.showUserManagement) {
        this.loadUsers();
      }
    }
  },
  computed: {
    isSuperAdmin() {
      return this.currentUser?.role === 'super_admin';
    },
    isAdmin() {
      return this.currentUser?.role === 'super_admin' || this.currentUser?.role === 'admin';
    },
    // Global filtered accounts based on date filter - used by all widgets
    globalFilteredAccounts() {
      if (!this.stats || !this.stats.accounts) return [];

      // Always return all accounts (including inactive ones)
      // This allows the table to show all accounts regardless of activity in the selected date range
      // Widgets can filter/sort accounts themselves as needed
      return [...this.stats.accounts];
    },
    // Cache account statuses - computed once per account, reused everywhere
    accountStatusCache() {
      const cache = {};
      if (!this.stats || !this.stats.accounts) return cache;
      this.stats.accounts.forEach(account => {
        if (account && account.formId) {
          cache[account.formId] = this.getAccountStatus(account);
        }
      });
      return cache;
    },
    filteredAccounts() {
      if (!this.globalFilteredAccounts || this.globalFilteredAccounts.length === 0) return [];
      
      // Access dateFilter to ensure Vue tracks it as a dependency for reactivity
      // This forces the computed property to recalculate when dateFilter changes
      // eslint-disable-next-line no-unused-expressions
      this.dateFilter;
      
      let accounts = [...this.globalFilteredAccounts];
      
      // Filter accounts based on activity in the selected date range
      // Only show accounts that have submissions in the selected period
      if (this.dateFilter !== 'all') {
        accounts = accounts.filter(account => {
          const filteredSubmissions = this.getFilteredSubmissions(account);
          // Keep account if it has any submissions in the selected date range
          return filteredSubmissions > 0;
        });
      }
      
      // Apply status filter (from Account Management stats clicks)
      if (this.statusFilter !== 'all') {
        accounts = accounts.filter(account => {
          return this.getAccountStatus(account) === this.statusFilter;
        });
      }
      
      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        accounts = accounts.filter(account => {
          const accountName = (account.accountName || account.formTitle || '').toLowerCase();
          return accountName.includes(query);
        });
      }
      
      // Apply sorting
      accounts.sort((a, b) => {
        let aValue, bValue;
        
        switch (this.sortColumn) {
          case 'accountName':
            aValue = (a.accountName || a.formTitle || '').toLowerCase();
            bValue = (b.accountName || b.formTitle || '').toLowerCase();
            break;
          case 'formId':
            aValue = parseInt(a.formId) || 0;
            bValue = parseInt(b.formId) || 0;
            break;
          case 'totalSubmissions':
            aValue = a.totalSubmissions || 0;
            bValue = b.totalSubmissions || 0;
            break;
          case 'last30DaysSubmissions':
            aValue = this.getFilteredSubmissions(a);
            bValue = this.getFilteredSubmissions(b);
            break;
          case 'previousMonthSubmissions':
            aValue = this.getPreviousMonthSubmissions(a);
            bValue = this.getPreviousMonthSubmissions(b);
            break;
          case 'change':
            aValue = this.getMonthChange(a);
            bValue = this.getMonthChange(b);
            break;
          case 'lastSubmission':
            aValue = a.lastSubmission ? new Date(a.lastSubmission).getTime() : 0;
            bValue = b.lastSubmission ? new Date(b.lastSubmission).getTime() : 0;
            break;
          case 'status':
            aValue = this.getAccountStatus(a);
            bValue = this.getAccountStatus(b);
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      return accounts;
    },
    paginatedAccounts() {
      if (!this.filteredAccounts || this.filteredAccounts.length === 0) return [];
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredAccounts.slice(start, end);
    },
    totalPages() {
      if (!this.filteredAccounts || this.filteredAccounts.length === 0) return 0;
      return Math.ceil(this.filteredAccounts.length / this.itemsPerPage);
    },
    pageStart() {
      if (!this.filteredAccounts || this.filteredAccounts.length === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    pageEnd() {
      if (!this.filteredAccounts || this.filteredAccounts.length === 0) return 0;
      const end = this.currentPage * this.itemsPerPage;
      return Math.min(end, this.filteredAccounts.length);
    },
    visiblePages() {
      if (!this.totalPages || this.totalPages === 0) return [];
      
      const pages = [];
      const maxVisible = 7;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    filteredSummaryStats() {
      if (!this.globalFilteredAccounts || this.globalFilteredAccounts.length === 0) {
        return {
          totalAccounts: 0,
          totalSubmissions: 0,
          averageSubmissions: 0
        };
      }
      
      const totalSubmissions = this.globalFilteredAccounts.reduce((sum, acc) => {
        return sum + this.getFilteredSubmissions(acc);
      }, 0);
      
      const averageSubmissions = this.globalFilteredAccounts.length > 0
        ? Math.round(totalSubmissions / this.globalFilteredAccounts.length)
        : 0;
      
      return {
        totalAccounts: this.globalFilteredAccounts.length,
        totalSubmissions,
        averageSubmissions
      };
    },
    topAccountsByTotal() {
      if (!this.globalFilteredAccounts || this.globalFilteredAccounts.length === 0) return [];
      
      // Separate active and inactive accounts
      const activeAccounts = this.globalFilteredAccounts.filter(acc => this.getAccountStatus(acc) === 'active');
      const inactiveAccounts = this.globalFilteredAccounts.filter(acc => this.getAccountStatus(acc) === 'inactive');
      
      // Sort active accounts by total submissions (descending)
      const sortedActive = [...activeAccounts].sort((a, b) => (b.totalSubmissions || 0) - (a.totalSubmissions || 0));
      
      // Sort inactive accounts by total submissions (descending) - inactive accounts with more historical submissions first
      const sortedInactive = [...inactiveAccounts].sort((a, b) => (b.totalSubmissions || 0) - (a.totalSubmissions || 0));
      
      // Combine: show top active accounts, but ensure inactive accounts are also included
      const result = [];
      
      // If we have inactive accounts, show top 4 active + at least 1 inactive to ensure visibility
      // Otherwise, show top 5 active accounts
      if (sortedInactive.length > 0) {
        // Show top 4 active accounts + 1 inactive account
        result.push(...sortedActive.slice(0, 4));
        result.push(sortedInactive[0]);
      } else {
        // No inactive accounts, just show top 5 active
        result.push(...sortedActive.slice(0, 5));
      }
      
      return result;
    },
    topAccountsBy30Days() {
      if (!this.globalFilteredAccounts || this.globalFilteredAccounts.length === 0) return [];
      
      // Map accounts with filtered submissions
      const accountsWithSubmissions = this.globalFilteredAccounts.map(account => ({
        ...account,
        filteredSubmissions: this.getFilteredSubmissions(account)
      }));
      
      // Separate active and inactive accounts
      const activeAccounts = accountsWithSubmissions.filter(acc => this.getAccountStatus(acc) === 'active');
      const inactiveAccounts = accountsWithSubmissions.filter(acc => this.getAccountStatus(acc) === 'inactive');
      
      // Sort active accounts by filtered submissions (descending)
      const sortedActive = [...activeAccounts].sort((a, b) => b.filteredSubmissions - a.filteredSubmissions);
      
      // Sort inactive accounts by filtered submissions (descending)
      const sortedInactive = [...inactiveAccounts].sort((a, b) => b.filteredSubmissions - a.filteredSubmissions);
      
      // Combine: show top active accounts, but ensure inactive accounts are also included
      const result = [];
      
      // If we have inactive accounts, show top 4 active + at least 1 inactive to ensure visibility
      // Otherwise, show top 5 active accounts
      if (sortedInactive.length > 0) {
        // Show top 4 active accounts + 1 inactive account
        result.push(...sortedActive.slice(0, 4));
        result.push(sortedInactive[0]);
      } else {
        // No inactive accounts, just show top 5 active
        result.push(...sortedActive.slice(0, 5));
      }
      
      return result;
    },
    accountActivityStats() {
      // Use ALL accounts from stats, not just filtered ones, to properly calculate inactive accounts
      const allAccounts = this.stats?.accounts || [];
      if (!allAccounts || allAccounts.length === 0) {
        return {
          active: 0,
          inactive: 0,
          activePercentage: 0,
          inactivePercentage: 0,
          avgDaysSinceLastActivity: 0,
          inactiveAccountsBreakdown: {
            recent: 0, // 1-30 days
            moderate: 0, // 31-60 days
            long: 0, // 61-90 days
            veryLong: 0 // 90+ days
          }
        };
      }
      
      // Use getAccountStatus() to get accurate status from Gravity Forms or calculated status
      // This ensures we're using the actual account status, not just filtered submission counts
      const active = allAccounts.filter(account => this.getAccountStatus(account) === 'active').length;
      const inactive = allAccounts.filter(account => this.getAccountStatus(account) === 'inactive').length;
      const total = allAccounts.length;
      
      // Calculate days since last activity for inactive accounts (check all accounts)
      const today = new Date();
      const inactiveAccounts = allAccounts.filter(account => {
        // Account is inactive based on actual status from Gravity Forms
        return this.getAccountStatus(account) === 'inactive';
      });
      let totalDaysSince = 0;
      const breakdown = { recent: 0, moderate: 0, long: 0, veryLong: 0 };
      
      inactiveAccounts.forEach(account => {
        if (account.lastSubmission) {
          const lastDate = new Date(account.lastSubmission);
          const daysSince = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
          totalDaysSince += daysSince;
          
          if (daysSince <= 30) breakdown.recent++;
          else if (daysSince <= 60) breakdown.moderate++;
          else if (daysSince <= 90) breakdown.long++;
          else breakdown.veryLong++;
        } else {
          // No submissions ever
          breakdown.veryLong++;
        }
      });
      
      const avgDaysSince = inactiveAccounts.length > 0 
        ? Math.round(totalDaysSince / inactiveAccounts.length) 
        : 0;
      
      return {
        active,
        inactive,
        activePercentage: total > 0 ? Math.round((active / total) * 100) : 0,
        inactivePercentage: total > 0 ? Math.round((inactive / total) * 100) : 0,
        avgDaysSinceLastActivity: avgDaysSince,
        inactiveAccountsBreakdown: breakdown
      };
    },
    filteredAccountCount() {
      // Always show total accounts count (all accounts are visible in table)
      // The date filter only affects submission counts displayed, not account visibility
      return this.stats?.accounts?.length || 0;
    },
    filteredAccountActivityStats() {
      // For 'all' filter, use all accounts. For other filters, use accounts with activity in that period
      let accountsToUse;
      if (this.dateFilter === 'all') {
        accountsToUse = this.stats?.accounts || [];
      } else {
        // Use accounts that have submissions in the selected date range
        accountsToUse = this.globalFilteredAccounts.filter(account => {
          return this.getFilteredSubmissions(account) > 0;
        });
      }
      
      if (!accountsToUse || accountsToUse.length === 0) {
        return {
          active: 0,
          inactive: 0,
          activePercentage: 0,
          inactivePercentage: 0,
          avgDaysSinceLastActivity: 0,
          inactiveAccountsBreakdown: {
            recent: 0,
            moderate: 0,
            long: 0,
            veryLong: 0
          }
        };
      }
      
      // For filtered date ranges, active = accounts with submissions in that range
      // Inactive = accounts that exist but have no submissions in that range
      if (this.dateFilter === 'all') {
        // Use account status for 'all' filter
        const active = accountsToUse.filter(account => this.getAccountStatus(account) === 'active').length;
        const inactive = accountsToUse.filter(account => this.getAccountStatus(account) === 'inactive').length;
        const total = accountsToUse.length;
        
        // Calculate inactive breakdown
        const today = new Date();
        const inactiveAccounts = accountsToUse.filter(account => this.getAccountStatus(account) === 'inactive');
        let totalDaysSince = 0;
        const breakdown = { recent: 0, moderate: 0, long: 0, veryLong: 0 };
        
        inactiveAccounts.forEach(account => {
          if (account.lastSubmission) {
            const lastDate = new Date(account.lastSubmission);
            const daysSince = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
            totalDaysSince += daysSince;
            
            if (daysSince <= 30) breakdown.recent++;
            else if (daysSince <= 60) breakdown.moderate++;
            else if (daysSince <= 90) breakdown.long++;
            else breakdown.veryLong++;
          } else {
            breakdown.veryLong++;
          }
        });
        
        const avgDaysSince = inactiveAccounts.length > 0 
          ? Math.round(totalDaysSince / inactiveAccounts.length) 
          : 0;
        
        return {
          active,
          inactive,
          activePercentage: total > 0 ? Math.round((active / total) * 100) : 0,
          inactivePercentage: total > 0 ? Math.round((inactive / total) * 100) : 0,
          avgDaysSinceLastActivity: avgDaysSince,
          inactiveAccountsBreakdown: breakdown
        };
      } else {
        // For date filters, active = accounts with submissions in that range
        // Inactive = accounts that exist but have 0 submissions in that range
        const active = accountsToUse.length;
        const allAccounts = this.stats?.accounts || [];
        const inactive = allAccounts.length - active;
        const total = allAccounts.length;
        
        // For filtered ranges, inactive breakdown is based on accounts not in the filtered set
        const today = new Date();
        const inactiveAccounts = allAccounts.filter(account => {
          return this.getFilteredSubmissions(account) === 0;
        });
        let totalDaysSince = 0;
        const breakdown = { recent: 0, moderate: 0, long: 0, veryLong: 0 };
        
        inactiveAccounts.forEach(account => {
          if (account.lastSubmission) {
            const lastDate = new Date(account.lastSubmission);
            const daysSince = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
            totalDaysSince += daysSince;
            
            if (daysSince <= 30) breakdown.recent++;
            else if (daysSince <= 60) breakdown.moderate++;
            else if (daysSince <= 90) breakdown.long++;
            else breakdown.veryLong++;
          } else {
            breakdown.veryLong++;
          }
        });
        
        const avgDaysSince = inactiveAccounts.length > 0 
          ? Math.round(totalDaysSince / inactiveAccounts.length) 
          : 0;
        
        return {
          active,
          inactive,
          activePercentage: total > 0 ? Math.round((active / total) * 100) : 0,
          inactivePercentage: total > 0 ? Math.round((inactive / total) * 100) : 0,
          avgDaysSinceLastActivity: avgDaysSince,
          inactiveAccountsBreakdown: breakdown
        };
      }
    },
    submissionConcentration() {
      if (!this.globalFilteredAccounts || this.globalFilteredAccounts.length === 0) {
        return {
          top20Percent: 0,
          top5Accounts: 0,
          concentrationRisk: 'low'
        };
      }
      
      const sortedAccounts = [...this.globalFilteredAccounts]
        .sort((a, b) => this.getFilteredSubmissions(b) - this.getFilteredSubmissions(a));
      
      const totalSubmissions = this.filteredSummaryStats.totalSubmissions;
      if (totalSubmissions === 0) {
        return { top20Percent: 0, top5Accounts: 0, concentrationRisk: 'low' };
      }
      
      // Top 20% of accounts
      const top20PercentCount = Math.ceil(this.globalFilteredAccounts.length * 0.2);
      const top20PercentSubmissions = sortedAccounts
        .slice(0, top20PercentCount)
        .reduce((sum, acc) => sum + this.getFilteredSubmissions(acc), 0);
      const top20Percent = Math.round((top20PercentSubmissions / totalSubmissions) * 100);
      
      // Top 5 accounts
      const top5Submissions = sortedAccounts
        .slice(0, 5)
        .reduce((sum, acc) => sum + this.getFilteredSubmissions(acc), 0);
      const top5Accounts = Math.round((top5Submissions / totalSubmissions) * 100);
      
      // Determine risk level
      let concentrationRisk = 'low';
      if (top20Percent >= 80) concentrationRisk = 'high';
      else if (top20Percent >= 60) concentrationRisk = 'medium';
      
      return {
        top20Percent,
        top5Accounts,
        concentrationRisk
      };
    },
    accountHealthTrends() {
      if (!this.globalFilteredAccounts || this.globalFilteredAccounts.length === 0) {
        return {
          growing: [],
          declining: [],
          stable: [],
          newAccounts: []
        };
      }
      
      const accounts = this.globalFilteredAccounts;
      const average30Days = this.filteredSummaryStats.averageSubmissions || 0;
      
      // Categorize accounts based on activity patterns
      const growing = [];
      const declining = [];
      const stable = [];
      const newAccounts = [];
      
      accounts.forEach(account => {
        const filteredSubmissions = this.getFilteredSubmissions(account);
        const total = account.totalSubmissions || 0;
        const avgPer30Days = total > 0 ? (total / (365 / 30)) : 0; // Rough estimate of historical 30-day average
        
        // New accounts (recent activity but low total)
        if (filteredSubmissions > 0 && total < 10) {
          newAccounts.push(account);
        }
        // Growing: recent activity significantly higher than historical average
        else if (filteredSubmissions > avgPer30Days * 1.2 && filteredSubmissions > average30Days) {
          growing.push(account);
        }
        // Declining: had activity before but recent is low
        else if (total > 50 && filteredSubmissions < avgPer30Days * 0.5 && filteredSubmissions < average30Days) {
          declining.push(account);
        }
        // Stable: consistent activity
        else if (filteredSubmissions > 0) {
          stable.push(account);
        }
      });
      
      return {
        growing: growing.slice(0, 5), // Top 5 growing
        declining: declining.slice(0, 5), // Top 5 declining
        stable: stable.slice(0, 5), // Top 5 stable
        newAccounts: newAccounts.slice(0, 5) // Top 5 new
      };
    },
    averageInsights() {
      if (!this.globalFilteredAccounts || this.globalFilteredAccounts.length === 0) {
        return {
          median: 0,
          min: 0,
          max: 0,
          aboveAverage: 0,
          aboveAveragePercentage: 0,
          distribution: {
            zeroTo25: 0,
            twentySixTo50: 0,
            fiftyOneTo100: 0,
            over100: 0
          }
        };
      }
      
      const accounts = this.globalFilteredAccounts;
      const average = this.filteredSummaryStats.averageSubmissions || 0;
      
      // Get all submission counts
      const submissions = accounts
        .map(acc => this.getFilteredSubmissions(acc))
        .sort((a, b) => a - b);
      
      // Calculate median
      const median = submissions.length > 0
        ? submissions.length % 2 === 0
          ? ((submissions[Math.floor(submissions.length / 2) - 1] + submissions[Math.floor(submissions.length / 2)]) / 2).toFixed(1)
          : submissions[Math.floor(submissions.length / 2)].toFixed(1)
        : 0;
      
      // Min and max
      const min = submissions.length > 0 ? submissions[0] : 0;
      const max = submissions.length > 0 ? submissions[submissions.length - 1] : 0;
      
      // Accounts above average
      const aboveAverage = accounts.filter(acc => this.getFilteredSubmissions(acc) > average).length;
      const aboveAveragePercentage = accounts.length > 0
        ? Math.round((aboveAverage / accounts.length) * 100)
        : 0;
      
      // Distribution breakdown
      const distribution = {
        zeroTo25: accounts.filter(acc => {
          const subs = this.getFilteredSubmissions(acc);
          return subs >= 0 && subs <= 25;
        }).length,
        twentySixTo50: accounts.filter(acc => {
          const subs = this.getFilteredSubmissions(acc);
          return subs >= 26 && subs <= 50;
        }).length,
        fiftyOneTo100: accounts.filter(acc => {
          const subs = this.getFilteredSubmissions(acc);
          return subs >= 51 && subs <= 100;
        }).length,
        over100: accounts.filter(acc => this.getFilteredSubmissions(acc) > 100).length
      };
      
      return {
        median,
        min,
        max,
        aboveAverage,
        aboveAveragePercentage,
        distribution
      };
    },
    emailRecipientsTooltip() {
      const recipients = this.emailSettings?.recipients?.trim();
      if (recipients) {
        return `Email Report to ${recipients}`;
      }
      return 'Email Report (configure recipients in Report Settings)';
    },
    accountsWithDashboardLinks() {
      if (!this.stats?.accounts) return 0;
      return this.stats.accounts.filter(account => account.accountSlug).length;
    },
    managementAccounts() {
      // Show ALL accounts in the management section, prioritizing inactive accounts first
      // This ensures all inactive accounts are visible in the admin panel
      if (!this.globalFilteredAccounts || this.globalFilteredAccounts.length === 0) return [];
      
      const accounts = [...this.globalFilteredAccounts];
      
      // Separate active and inactive accounts
      const activeAccounts = accounts.filter(acc => this.getAccountStatus(acc) === 'active');
      const inactiveAccounts = accounts.filter(acc => this.getAccountStatus(acc) === 'inactive');
      
      // Show inactive accounts first (so they're visible), then active accounts
      // This ensures all accounts are shown, with inactive accounts prioritized
      return [
        ...inactiveAccounts,
        ...activeAccounts
      ];
    },
    filteredManagementAccounts() {
      // Filter management accounts by search query and status filter
      let accounts = [...this.managementAccounts];
      
      // Apply status filter
      if (this.statusFilter !== 'all') {
        accounts = accounts.filter(account => {
          return this.getAccountStatus(account) === this.statusFilter;
        });
      }
      
      // Apply search filter
      if (this.managementSearchQuery.trim()) {
        const query = this.managementSearchQuery.toLowerCase().trim();
        accounts = accounts.filter(account => {
          const accountName = (account.accountName || account.formTitle || '').toLowerCase();
          const formId = String(account.formId || '').toLowerCase();
          return accountName.includes(query) || formId.includes(query);
        });
      }
      
      return accounts;
    }
  },
  async mounted() {
    // Check Better Auth authentication
    this.isCheckingAuth = true;
    try {
      this.isAuthenticated = await auth.isAuthenticated();
      if (this.isAuthenticated) {
        // Get current user from session (includes role)
        const session = await auth.getSession();
        if (session && session.user) {
          this.currentUser = session.user;
        } else {
          // Fallback to getUser if session doesn't have user
          this.currentUser = await auth.getUser();
        }
        
        // Load saved email settings
        this.loadEmailSettings();
        
        // Load sent reports history
        this.loadSentReportsHistory();
        
        // Fetch stats immediately
        this.fetchStats();
      }
    } catch (error) {
      console.error('Auth check error:', error);
      this.isAuthenticated = false;
    } finally {
      this.isCheckingAuth = false;
    }
  },
  beforeDestroy() {
    // Clear any pending save timeout
    if (this.settingsSaveTimeout) {
      clearTimeout(this.settingsSaveTimeout);
    }
  },
  methods: {
    async handleLogout() {
      try {
        await auth.signOut();
        this.isAuthenticated = false;
        this.$router.push('/admin/login');
      } catch (error) {
        console.error('Logout error:', error);
        // Force logout even if API call fails
        this.isAuthenticated = false;
        this.$router.push('/admin/login');
      }
    },
    async loadUsers() {
      this.isLoadingUsers = true;
      try {
        this.users = await auth.listUsers();
        // Also load current user info to check role
        const session = await auth.getSession();
        if (session && session.user) {
          this.currentUser = session.user;
        }
      } catch (error) {
        console.error('Error loading users:', error);
        this.userCreationError = 'Failed to load users';
      } finally {
        this.isLoadingUsers = false;
      }
    },
    async createUser() {
      this.isCreatingUser = true;
      this.userCreationError = null;
      this.userCreationSuccess = false;

      try {
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const url = (isLocal && window.location.port === '8888')
          ? 'http://localhost:8888/.netlify/functions/create-admin'
          : '/.netlify/functions/create-admin';

        const response = await axios.post(url, {
          email: this.newUserEmail,
          password: this.newUserPassword,
          name: this.newUserName || this.newUserEmail.split('@')[0],
          role: this.isAdmin ? this.newUserRole : 'user'
        });

        if (response.data.success) {
          this.userCreationSuccess = true;
          // Reset form
          this.newUserEmail = '';
          this.newUserPassword = '';
          this.newUserName = '';
          // Reload users list
          await this.loadUsers();
          // Switch to list tab
          setTimeout(() => {
            this.userManagementTab = 'list';
            this.userCreationSuccess = false;
          }, 1500);
        }
      } catch (error) {
        if (error.response?.status === 409) {
          this.userCreationError = 'User already exists with this email address';
        } else {
          this.userCreationError = error.response?.data?.error || error.message || 'Failed to create user';
        }
      } finally {
        this.isCreatingUser = false;
      }
    },
    editUser(user) {
      // Prevent non-super-admins from editing super admins
      if (user.role === 'super_admin' && !this.isSuperAdmin) {
        alert('Super admins can only be edited by other super admins.');
        return;
      }
      this.editingUser = { ...user };
      this.editingUserRole = user.role || 'user';
      this.editingUserPassword = '';
      this.userUpdateError = null;
      this.userUpdateSuccess = false;
      this.userManagementTab = 'edit';
    },
    cancelEdit() {
      this.editingUser = null;
      this.editingUserRole = 'user';
      this.editingUserPassword = '';
      this.userUpdateError = null;
      this.userUpdateSuccess = false;
      this.userManagementTab = 'list';
    },
    async saveUser() {
      // Prevent non-super-admins from editing super admins
      if (this.editingUser.role === 'super_admin' && !this.isSuperAdmin) {
        this.userUpdateError = 'Super admins can only be edited by other super admins.';
        return;
      }

      this.isUpdatingUser = true;
      this.userUpdateError = null;
      this.userUpdateSuccess = false;

      try {
        const updates = {
          email: this.editingUser.email,
          name: this.editingUser.name,
          role: this.editingUserRole
        };

        // Only super admins can set role to super_admin
        if (updates.role === 'super_admin' && !this.isSuperAdmin) {
          this.userUpdateError = 'Only super admins can set role to super admin.';
          this.isUpdatingUser = false;
          return;
        }

        if (this.editingUserPassword) {
          updates.password = this.editingUserPassword;
        }

        await auth.updateUser(this.editingUser.id, updates);
        
        this.userUpdateSuccess = true;
        await this.loadUsers();
        
        setTimeout(() => {
          this.cancelEdit();
        }, 1500);
      } catch (error) {
        this.userUpdateError = error.response?.data?.error || error.message || 'Failed to update user';
        console.error('Error updating user:', error);
      } finally {
        this.isUpdatingUser = false;
      }
    },
    confirmDeleteUser(user) {
      // Prevent deleting super admins
      if (user.role === 'super_admin') {
        alert('Super admins cannot be deleted for security reasons.');
        return;
      }
      
      if (user.email === this.currentUser?.email) {
        alert('Cannot delete your own account');
        return;
      }
      
      if (confirm(`Are you sure you want to delete ${user.email}? This action cannot be undone.`)) {
        this.deleteUser(user);
      }
    },
    async deleteUser(user) {
      try {
        await auth.deleteUser(user.id, user.email);
        await this.loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(error.response?.data?.error || error.message || 'Failed to delete user');
      }
    },
    async handleChangePassword() {
      // Validate passwords match
      if (this.changePasswordNew !== this.changePasswordConfirm) {
        this.changePasswordError = 'New passwords do not match';
        this.changePasswordSuccess = false;
        return;
      }

      // Validate password length
      if (this.changePasswordNew.length < 8) {
        this.changePasswordError = 'New password must be at least 8 characters long';
        this.changePasswordSuccess = false;
        return;
      }

      this.isChangingPassword = true;
      this.changePasswordError = null;
      this.changePasswordSuccess = false;

      try {
        await auth.changePassword(this.changePasswordCurrent, this.changePasswordNew);
        this.changePasswordSuccess = 'Password changed successfully! You can now log in with your new password.';
        this.changePasswordError = null;
        
        // Clear form fields
        this.changePasswordCurrent = '';
        this.changePasswordNew = '';
        this.changePasswordConfirm = '';
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.changePasswordSuccess = false;
        }, 3000);
      } catch (error) {
        this.changePasswordError = error.response?.data?.error || error.message || 'Failed to change password';
        this.changePasswordSuccess = false;
        console.error('Error changing password:', error);
      } finally {
        this.isChangingPassword = false;
      }
    },
    async handleAuthenticated() {
      this.isAuthenticated = true;
      // Load current user info (includes role)
      try {
        const session = await auth.getSession();
        if (session && session.user) {
          this.currentUser = session.user;
        }
      } catch (error) {
        console.error('Error loading current user:', error);
      }
      this.loadEmailSettings();
      this.loadSentReportsHistory();
      this.fetchStats();
    },
    async fetchAdminPostId() {
      // Fetch WordPress post ID from slug using REST API
      try {
        const response = await axios.get(
          'https://engagr.driveagent.io/wp-json/wp/v2/pages',
          {
            params: {
              slug: 'admin',
              _fields: 'id'
            }
          }
        );
        
        if (response.data && response.data.length > 0) {
          this.adminPostId = response.data[0].id;
        } else {
          console.error('Admin page not found in WordPress. Make sure the page with slug "admin" exists.');
        }
      } catch (error) {
        console.error('Error fetching admin post ID:', error);
        // Fallback to environment variable if API call fails
        this.adminPostId = process.env.VUE_APP_ADMIN_POST_ID || null;
      }
    },
    async fetchStats(forceRefresh = false) {
      // Progressive Loading Strategy (using Netlify Blobs):
      // Step 1: Load cache immediately (<1s) to show data instantly
      // Step 2: Background sync if cache is stale (>10 min)
      
      if (!this.hasLoadedCache && !forceRefresh) {
        this.isLoading = true;
        this.error = null;
        this.loadingStartTime = Date.now();
        this.loadingMessage = 'Loading dashboard...';
        this.loadingSubMessage = 'Checking for cached data';
        
        try {
          await this.loadCachedData();
          this.hasLoadedCache = true;
          
          // If we got cache, show it immediately
          if (this.stats && this.stats.summary && this.stats.summary.totalAccounts > 0) {
            this.isLoading = false;
            
            // Trigger background sync if cache is stale
            if (this.stats.stale || this.stats.noCache) {
              setTimeout(() => {
                this.syncFreshData();
              }, 500);
            }
            return;
          }
        } catch {
          // Silently fail cache load - will load fresh data instead
        }
      }
      
      // Step 2: Load fresh data (blocking only if no cache was loaded)
      if (!this.stats || this.stats.summary.totalAccounts === 0 || forceRefresh) {
        this.isLoading = true;
        this.error = null;
        this.loadingStartTime = Date.now();
        
        // Update loading messages with helpful context
        this.loadingMessage = 'Loading dashboard data...';
        this.loadingSubMessage = 'Fetching forms from Gravity Forms API';
        
        // Show estimated time after 3 seconds
        const estimateTimer = setTimeout(() => {
          if (this.isLoading) {
            const elapsed = Math.floor((Date.now() - this.loadingStartTime) / 1000);
            this.loadingMessage = 'Processing account data...';
            this.loadingSubMessage = `This may take 15-30 seconds for first load (${elapsed}s elapsed)`;
          }
        }, 3000);
        
        // Update message if taking longer
        const longLoadTimer = setTimeout(() => {
          if (this.isLoading) {
            const elapsed = Math.floor((Date.now() - this.loadingStartTime) / 1000);
            this.loadingMessage = 'Almost done...';
            this.loadingSubMessage = `Aggregating statistics (${elapsed}s elapsed)`;
          }
        }, 10000);

        try {
          await this.syncFreshData(forceRefresh);
          
          // Clear timers
          clearTimeout(estimateTimer);
          clearTimeout(longLoadTimer);
          
          // Show success message briefly
          const loadTime = Math.floor((Date.now() - this.loadingStartTime) / 1000);
          this.loadingMessage = 'Data loaded successfully!';
          this.loadingSubMessage = `Loaded in ${loadTime} seconds${this.stats.cached ? ' (from cache)' : ''}`;
          
        } catch (error) {
          // Clear timers on error
          clearTimeout(estimateTimer);
          clearTimeout(longLoadTimer);
          console.error('Error fetching admin stats:', error);
          console.error('Error response:', error.response?.data);
          
          const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to load admin statistics';
          const errorDetails = error.response?.data?.details ? `\n\nDetails: ${JSON.stringify(error.response.data.details)}` : '';
          this.error = errorMessage + errorDetails;
        } finally {
          this.isLoading = false;
        }
      }
    },
    
    async loadCachedData() {
      const functionUrl = this.getFunctionUrl();
      const sessionToken = auth.sessionToken || localStorage.getItem('better-auth.session_token');
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (sessionToken) {
        headers.Authorization = `Bearer ${sessionToken}`;
      }
      
      const response = await axios.post(functionUrl, {
        loadCacheOnly: true
      }, { headers });
      
      if (response.data && !response.data.error) {
        this.stats = response.data;
      }
    },
    
    async syncFreshData(forceRefresh = false) {
      this.isSyncing = true;
      this.loadingSubMessage = forceRefresh ? 'Force refreshing from Gravity Forms...' : 'Loading data...';
      
      try {
        const functionUrl = this.getFunctionUrl();
        const sessionToken = auth.sessionToken || localStorage.getItem('better-auth.session_token');
        
        const headers = {
          'Content-Type': 'application/json'
        };
        
        if (sessionToken) {
          headers.Authorization = `Bearer ${sessionToken}`;
        }
        
        // Use cache for faster loads (only force refresh when explicitly requested)
        // Cache is fresh for 10 minutes, which is sufficient for dashboard stats
        const response = await axios.post(functionUrl, {
          forceRefresh: forceRefresh // Use cached data by default, force only when requested
        }, { headers });
        
        if (response.data.error) {
          throw new Error(response.data.error);
        }
        
        this.stats = response.data;
      } catch (error) {
        console.error('Error syncing fresh data:', error);
        console.error('Error response:', error.response);
        console.error('Error message:', error.message);
        
        // Extract error message from response if available
        let errorMessage = 'Failed to sync data. Please try again.';
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
          if (error.response.data.message) {
            errorMessage += `: ${error.response.data.message}`;
          }
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.error = errorMessage;
      } finally {
        this.isSyncing = false;
      }
    },
    
    getFunctionUrl() {
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      return isDev && window.location.port === '8888'
        ? 'http://localhost:8888/.netlify/functions/admin-stats'
        : '/.netlify/functions/admin-stats';
    },
    
    async forceRefresh() {
      await this.fetchStats(true);
    },
    formatNumber(num) {
      return new Intl.NumberFormat('en-US').format(num);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    formatCacheAge(milliseconds) {
      if (!milliseconds) return 'unknown';
      const seconds = Math.floor(milliseconds / 1000);
      const minutes = Math.floor(seconds / 60);
      
      if (minutes < 1) return `${seconds}s`;
      if (minutes < 60) return `${minutes}m`;
      
      const hours = Math.floor(minutes / 60);
      return `${hours}h ${minutes % 60}m`;
    },
    formatLastSyncTime() {
      if (!this.stats || !this.stats.timestamp) {
        return 'Unknown';
      }
      
      const syncDate = new Date(this.stats.timestamp);
      const now = new Date();
      
      // Calculate time ago
      const diffMs = now - syncDate;
      const diffMins = Math.floor(diffMs / 60000);
      
      // Format the actual time
      const timeStr = syncDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      const dateStr = syncDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      
      // Show relative time if recent
      if (diffMins < 1) {
        return `${timeStr} (just now)`;
      } else if (diffMins < 60) {
        return `${timeStr} (${diffMins}m ago)`;
      } else {
        // Check if today
        const isToday = syncDate.toDateString() === now.toDateString();
        if (isToday) {
          return `${timeStr} today`;
        } else {
          return `${timeStr}, ${dateStr}`;
        }
      }
    },
    getDateRangeLabel() {
      const { startDate, endDate } = this.getDateRangeForFilter();
      
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      };
      
      if (this.dateFilter === 'all') {
        return 'All Time';
      }
      
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    },
    getDateFilterLabel() {
      const labels = {
        'all': 'All Time',
        'last7': 'Last 7 Days',
        'last30': 'Last 30 Days',
        'thisMonth': 'This Month',
        'lastMonth': 'Last Month',
        'last12Months': 'Last 12 Months'
      };
      return labels[this.dateFilter] || 'Last 30 Days';
    },
    getPreviousPeriodLabel() {
      // Returns the label for the "Previous Period" column based on current filter
      const labels = {
        'all': 'Previous Year',
        'last7': 'Previous 7 Days',
        'last30': 'Previous 30 Days',
        'thisMonth': 'Previous Month',
        'lastMonth': 'Previous Month',
        'last12Months': 'Previous 12 Months'
      };
      return labels[this.dateFilter] || 'Previous Period';
    },
    getPreviousPeriodSubtitle() {
      // Returns the subtitle for the "Previous Period" column based on current filter
      const labels = {
        'all': 'Last Year',
        'last7': 'Same period last month',
        'last30': 'Same period last month',
        'thisMonth': 'Last Month',
        'lastMonth': 'Month before',
        'last12Months': 'Previous period'
      };
      return labels[this.dateFilter] || 'Comparison';
    },
    getDateRangeForFilter() {
      const today = new Date();
      let startDate, endDate;

      switch (this.dateFilter) {
        case 'all':
          endDate = new Date(today);
          startDate = new Date(2000, 0, 1); // January 1, 2000
          break;

        case 'last12Months':
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setMonth(startDate.getMonth() - 12);
          break;

        case 'last7':
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 6); // 7 days including today
          break;

        case 'last30':
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 29); // 30 days including today
          break;

        case 'thisMonth':
          endDate = new Date(today);
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;

        case 'lastMonth': {
          const lastMonth = today.getMonth() - 1;
          const yearOfLastMonth = lastMonth < 0 ? today.getFullYear() - 1 : today.getFullYear();
          const monthIndex = lastMonth < 0 ? 11 : lastMonth;
          startDate = new Date(yearOfLastMonth, monthIndex, 1);
          // Get last day of the previous month by using day 0 of the next month
          endDate = new Date(yearOfLastMonth, monthIndex + 1, 0, 23, 59, 59, 999);
          break;
        }

        default:
          endDate = new Date(today);
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 29);
      }

      return { startDate, endDate };
    },
    hasActivityInDateRange(account) {
      if (!account.lastSubmission) return false;
      
      const lastSubmissionDate = new Date(account.lastSubmission);
      const { startDate, endDate } = this.getDateRangeForFilter();
      
      // Normalize dates for comparison - compare dates only, ignoring time
      const submissionDateOnly = new Date(lastSubmissionDate.getFullYear(), lastSubmissionDate.getMonth(), lastSubmissionDate.getDate());
      const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      
      // Check if submission date falls within the range (inclusive)
      return submissionDateOnly >= startDateOnly && submissionDateOnly <= endDateOnly;
    },
    getFilteredSubmissions(account) {
      try {
        // For 'all' filter, show current year submissions instead of all-time
        if (this.dateFilter === 'all') {
        const total = account.totalSubmissions || 0;
        if (total === 0) return 0;
        
        // Estimate current year submissions
        // Method: Calculate based on proportion of year elapsed and account's activity pattern
        const today = new Date();
        const currentYear = today.getFullYear();
        const yearStart = new Date(currentYear, 0, 1);
        const daysInYear = 365;
        const daysElapsed = Math.floor((today - yearStart) / (1000 * 60 * 60 * 24)) + 1;
        const yearProgress = daysElapsed / daysInYear;
        
        // If account has lastSubmission in current year, estimate current year submissions
        if (account.lastSubmission) {
          const lastSubmissionDate = new Date(account.lastSubmission);
          const lastYear = lastSubmissionDate.getFullYear();
          
          if (lastYear === currentYear) {
            // Account has activity this year - estimate based on proportion of year
            // Use a more sophisticated estimate: if they have last30DaysSubmissions, use that as a base
            const last30Days = account.last30DaysSubmissions || 0;
            if (last30Days > 0) {
              // Estimate: extrapolate last30Days to full year proportionally
              // But be conservative - don't assume they'll maintain the same rate
              const estimatedCurrentYear = Math.round((last30Days * 365) / 30);
              // Cap it at total submissions (can't exceed all-time total)
              return Math.min(estimatedCurrentYear, total);
            }
          }
        }
        
        // Fallback: Estimate current year as proportion of total based on account age
        // If account has been active for multiple years, estimate current year proportionally
        if (account.lastSubmission) {
          const lastSubmissionDate = new Date(account.lastSubmission);
          const accountStartYear = lastSubmissionDate.getFullYear();
          const yearsActive = Math.max(1, currentYear - accountStartYear + 1);
          
          // Estimate average yearly rate
          const avgYearlyRate = Math.round(total / yearsActive);
          
          // For current year, use proportion of year elapsed
          const estimatedCurrentYear = Math.round(avgYearlyRate * yearProgress);
          
          // Ensure it doesn't exceed total and is reasonable
          return Math.min(estimatedCurrentYear, total);
        }
        
        // Final fallback: Estimate based on year progress
        // Assume submissions are distributed evenly across account lifetime
        const estimatedCurrentYear = Math.round(total * yearProgress);
        return Math.min(estimatedCurrentYear, total);
      }
      
      // Get the date range for the current filter
      const { startDate, endDate } = this.getDateRangeForFilter();
      const today = new Date();
      
      // Calculate how many days ago the range ends
      const daysAgoEnd = Math.floor((today - endDate) / (1000 * 60 * 60 * 24));
      const daysAgoStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
      
      const totalSubs = account.totalSubmissions || 0;
      const last30Days = account.last30DaysSubmissions || 0;
      
      // Special handling for "last month" - use actual lastMonthSubmissions if available
      if (this.dateFilter === 'lastMonth') {
        // If backend provides lastMonthSubmissions, use it directly
        if (account.lastMonthSubmissions !== undefined && account.lastMonthSubmissions !== null) {
          return account.lastMonthSubmissions || 0;
        }
        
        // Fallback to estimation if lastMonthSubmissions is not available
        // Check if account had activity in last month
        const hadActivityLastMonth = this.hasActivityInDateRange(account);
        if (!hadActivityLastMonth) {
          // If lastSubmission is in current month, check if last30Days includes last month
          // If last30Days > 0 and we're early in current month, likely some were in last month
          const daysIntoCurrentMonth = today.getDate();
          if (last30Days > 0 && daysIntoCurrentMonth <= 10) {
            // Early in month, estimate that most of last30Days was in last month
            // Use a conservative estimate: assume 60-80% was in last month
            const estimatedLastMonth = Math.round(last30Days * 0.7);
            return estimatedLastMonth;
          }
          return 0;
        }
        // Account had activity in last month, use better estimation
        return this.estimateSubmissionsForLastMonth(account, startDate, endDate, totalSubs, last30Days);
      }
      
      // For date ranges that overlap with or are within the last 30 days
      // Check if the range overlaps with last 30 days
      if (daysAgoEnd <= 30) {
        // Special handling for "thisMonth" - can't use last30DaysSubmissions directly
        // because it includes days from previous month
        if (this.dateFilter === 'thisMonth') {
          // For thisMonth, estimate based on proportion of last30Days that falls in current month
          const today = new Date();
          const daysIntoMonth = today.getDate();
          
          // Estimate: if last30Days includes X days from previous month and Y days from current month,
          // estimate current month submissions proportionally
          if (last30Days > 0 && daysIntoMonth > 0) {
            // Estimate that submissions are distributed proportionally to days
            const estimatedThisMonth = Math.round((last30Days * daysIntoMonth) / 30);
            return Math.min(estimatedThisMonth, last30Days);
          }
          return 0;
        }
        
        // For last30 filter, if range is exactly the last 30 days, use last30DaysSubmissions directly
        if (this.dateFilter === 'last30' && daysAgoStart <= 30 && daysAgoEnd >= 0) {
          // last30 filter matches exactly with last30DaysSubmissions
          return last30Days;
        }
        
        // For last7 filter, estimate proportionally from last30DaysSubmissions
        // Only estimate if account has activity in last 30 days
        if (this.dateFilter === 'last7' && daysAgoStart <= 30 && daysAgoEnd >= 0) {
          // If account has no activity in last 30 days, return 0
          if (last30Days === 0) {
            return 0;
          }
          // Estimate: last7 is approximately 7/30 of last30DaysSubmissions
          // But use a slightly more conservative estimate to account for uneven distribution
          const estimatedLast7 = Math.round((last30Days * 7) / 30);
          // Ensure it's reasonable - shouldn't exceed last30Days
          return Math.min(estimatedLast7, last30Days);
        }
        
        // For last12Months filter, estimate based on last30DaysSubmissions if available
        // Otherwise estimate based on total submissions and account activity
        if (this.dateFilter === 'last12Months') {
          if (last30Days > 0) {
            // If account has recent activity, estimate 12 months based on last30Days
            // Estimate: extrapolate last30Days to 12 months (365 days)
            const estimated12Months = Math.round((last30Days * 365) / 30);
            // Cap at total submissions (can't exceed all-time total)
            return Math.min(estimated12Months, totalSubs);
          }
          // If no recent activity, estimate based on total submissions and account age
          if (account.lastSubmission) {
            const lastSubmissionDate = new Date(account.lastSubmission);
            const daysSinceLastSubmission = Math.floor((today - lastSubmissionDate) / (1000 * 60 * 60 * 24));
            const daysIn12Months = 365;
            
            // If account had activity within the last 12 months, estimate proportionally
            if (daysSinceLastSubmission <= daysIn12Months && totalSubs > 0) {
              // Estimate based on proportion of account's lifetime submissions
              const estimatedAccountAgeDays = Math.max(daysIn12Months, daysSinceLastSubmission + 30);
              const avgPerDay = totalSubs / estimatedAccountAgeDays;
              return Math.round(avgPerDay * daysIn12Months);
            }
          }
          // Account has no activity in last 12 months - return 0 (but account will still show in table)
          return 0;
        }
        
        // If range starts before last 30 days but ends within it, estimate
        return this.estimateSubmissionsForPeriod(account, startDate, endDate, totalSubs);
      }
      
      // For ranges completely outside last 30 days (like "last month" when we're well into current month)
      // We need to estimate since backend doesn't provide exact counts
      if (account.lastSubmission) {
        const lastSubmissionDate = new Date(account.lastSubmission);
        // Verify last submission was in the range (we already checked this, but double-check)
        const submissionDateOnly = new Date(lastSubmissionDate.getFullYear(), lastSubmissionDate.getMonth(), lastSubmissionDate.getDate());
        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        
        if (submissionDateOnly >= startDateOnly && submissionDateOnly <= endDateOnly) {
          return this.estimateSubmissionsForPeriod(account, startDate, endDate, totalSubs);
        }
      }
      
      return 0;
      } catch (error) {
        console.error('Error in getFilteredSubmissions:', error, account);
        return 0;
      }
    },
    estimateSubmissionsForLastMonth(account, startDate, endDate, totalSubs, last30Days) {
      // For last month specifically, we have last30DaysSubmissions which includes
      // both last month and current month. We need to estimate what portion was last month.
      
      const today = new Date();
      
      // Calculate how many days of the last 30 days were actually in last month
      // last30DaysSubmissions covers: (today - 29 days) to today
      // Last month is: startDate to endDate
      
      // Find the overlap between last 30 days and last month
      const last30DaysStart = new Date(today);
      last30DaysStart.setDate(today.getDate() - 29); // 30 days including today
      
      // Calculate overlap: max(startDate, last30DaysStart) to min(endDate, today)
      const overlapStart = new Date(Math.max(startDate.getTime(), last30DaysStart.getTime()));
      const overlapEnd = new Date(Math.min(endDate.getTime(), today.getTime()));
      
      // Calculate days in overlap
      const daysInOverlap = Math.max(0, Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24)) + 1);
      const totalDaysInLast30 = 30;
      
      // If there's an overlap and we have last30Days data, proportionally estimate
      if (daysInOverlap > 0 && last30Days > 0) {
        // Estimate: (days in overlap / 30) * last30DaysSubmissions
        const estimatedLastMonth = Math.round((last30Days * daysInOverlap) / totalDaysInLast30);
        
        // Ensure it's reasonable - shouldn't exceed last30Days or be less than 0
        return Math.max(0, Math.min(estimatedLastMonth, last30Days));
      }
      
      // If no overlap or no last30Days data, use general estimation
      return this.estimateSubmissionsForPeriod(account, startDate, endDate, totalSubs);
    },
    estimateSubmissionsForPeriod(account, startDate, endDate, totalSubs) {
      if (!account.lastSubmission) {
        return 0;
      }
      
      if (totalSubs === 0) {
        return 0;
      }
      
      const lastSubmissionDate = new Date(account.lastSubmission);
      const daysSinceLastSubmission = Math.floor((new Date() - lastSubmissionDate) / (1000 * 60 * 60 * 24));
      const daysInRange = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      
      // Note: We already verified the account has activity in this period via hasActivityInDateRange
      // So we can proceed with estimation
      
      // If they have recent activity (last30DaysSubmissions), use it as approximation
      // This works well if the period overlaps with recent activity
      const last30Days = account.last30DaysSubmissions || 0;
      if (last30Days > 0) {
        return last30Days;
      }
      
      // For accounts with activity in the period but no recent activity,
      // estimate based on their historical submission pattern
      
      // Calculate approximate monthly submission rate based on total submissions
      // Assume submissions are distributed over the account's lifetime
      // Use days since last submission as a proxy for account age
      const estimatedAccountAgeDays = Math.max(daysInRange, daysSinceLastSubmission + daysInRange);
      const avgPerDay = totalSubs / estimatedAccountAgeDays;
      let estimated = Math.round(avgPerDay * daysInRange);
      
      // For monthly periods (28-31 days), use a better estimation
      if (daysInRange >= 28 && daysInRange <= 31) {
        // Estimate monthly rate: if account has been active for multiple months,
        // calculate average monthly submissions
        const estimatedMonthsActive = Math.max(1, Math.floor(estimatedAccountAgeDays / 30));
        const avgPerMonth = Math.floor(totalSubs / estimatedMonthsActive);
        
        // Use the higher of the two estimates, but be reasonable
        estimated = Math.max(estimated, Math.min(avgPerMonth, totalSubs));
        
        // Ensure minimum: if account had activity in this period, they should have at least some submissions
        // Base minimum on total submissions - accounts with more totals likely had more activity
        if (totalSubs >= 50) {
          // High-volume accounts: estimate at least 5-10% of total for the month
          estimated = Math.max(estimated, Math.min(Math.floor(totalSubs * 0.1), 20));
        } else if (totalSubs >= 20) {
          // Medium accounts: estimate at least 10-20% of total
          estimated = Math.max(estimated, Math.min(Math.floor(totalSubs * 0.2), 10));
        } else {
          // Low-volume accounts: at least 1, but could be more
          estimated = Math.max(estimated, Math.min(Math.floor(totalSubs * 0.3), 5));
        }
      } else {
        // For non-monthly periods, ensure at least 1 if they had activity
        estimated = Math.max(1, estimated);
      }
      
      // Ensure we don't exceed total submissions
      return Math.min(estimated, totalSubs);
    },
    applyDateFilter(preset) {
      if (this.dateFilter === preset) {
        return; // Already set to this filter
      }
      this.dateFilter = preset;
      // Reset to first page when filter changes
      this.currentPage = 1;
    },
    getPercentageOfTotal(value, maxValue) {
      if (!maxValue || maxValue === 0 || !value) return 0;
      return Math.round((value / maxValue) * 100);
    },
    sortTable(column) {
      // If clicking the same column, toggle direction
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        // Default to ascending for text columns, descending for numeric/date columns
        const textColumns = ['accountName', 'status'];
        this.sortDirection = textColumns.includes(column) ? 'asc' : 'desc';
      }
    },
    // Use cached status when available (for template bindings)
    getCachedAccountStatus(account) {
      if (!account || !account.formId) return 'inactive';
      return this.accountStatusCache[account.formId] || this.getAccountStatus(account);
    },
    getAccountStatus(account) {
      if (!account) {
        return 'inactive';
      }
      
      // Priority 1: Use Gravity Forms is_active status (boolean/number/string field) - most reliable
      // In Gravity Forms: is_active: 1 = active, is_active: 0 = inactive
      if (account.is_active !== undefined && account.is_active !== null) {
        // Handle boolean, number, and string representations
        const isActive = account.is_active === true || 
                        account.is_active === 1 || 
                        account.is_active === '1' || 
                        account.is_active === 'true';
        return isActive ? 'active' : 'inactive';
      }
      
      // Priority 2: Fallback to status field if is_active is not available
      if (account.status !== undefined && account.status !== null) {
        const status = typeof account.status === 'string' 
          ? account.status.toLowerCase() 
          : String(account.status).toLowerCase();
        if (status === 'active' || status === 'inactive') {
          return status;
        }
      }
      
      // Priority 3: Internal checks as fallback (when GF doesn't provide status)
      // Only use these if Gravity Forms didn't provide a status

      // Internal check: Match Gravity Forms behavior
      // Accounts are inactive if last submission was > 90 days ago
      if (account.lastSubmission) {
        const lastSubmissionDate = new Date(account.lastSubmission);
        const today = new Date();
        const daysSinceLastSubmission = Math.floor((today - lastSubmissionDate) / (1000 * 60 * 60 * 24));
        
        // If last submission was more than 90 days ago → inactive (matches GF behavior)
        if (daysSinceLastSubmission > 90) {
          return 'inactive';
        }
        // If last submission was within last 90 days → active
        return 'active';
      }
      
      // No lastSubmission date: check if account has any submissions
      const totalSubmissions = account.totalSubmissions || 0;
      const last30DaysSubmissions = account.last30DaysSubmissions || 0;
      
      // If no submissions ever → inactive
      if (totalSubmissions === 0) {
        return 'inactive';
      }
      
      // Has submissions but no lastSubmission date
      // If has recent activity (last 30 days) → active
      if (last30DaysSubmissions > 0) {
        return 'active';
      }
      
      // Has submissions but no recent activity and no lastSubmission date → inactive
      // This catches accounts with old submissions that haven't been active recently
      return 'inactive';
    },
    getPreviousMonthSubmissions(account) {
      // Special case: When viewing "all" filter, "Previous Year" means last year's submissions
      if (this.dateFilter === 'all') {
        const total = account.totalSubmissions || 0;
        if (total === 0) return 0;
        
        // Estimate previous year's submissions
        // Method: Calculate average yearly rate based on account age and total submissions
        if (account.lastSubmission) {
          const lastSubmissionDate = new Date(account.lastSubmission);
          const today = new Date();
          
          // Calculate account age in years
          // Estimate account start as first submission date (use lastSubmission as proxy)
          // For a more accurate estimate, we'd need the first submission date, but we'll use
          // the assumption that accounts have been active for at least 1 year if they have submissions
          const yearsActive = Math.max(1, Math.ceil((today - lastSubmissionDate) / (1000 * 60 * 60 * 24 * 365)));
          
          // Calculate average yearly submission rate
          const avgYearlyRate = Math.round(total / yearsActive);
          
          // For previous year, use the average yearly rate
          // This assumes relatively consistent yearly activity
          return avgYearlyRate;
        }
        
        // Fallback: If no lastSubmission date, estimate based on total submissions
        // Assume account has been active for at least 2 years
        const estimatedYearsActive = Math.max(2, Math.ceil(total / 200)); // Rough estimate
        const avgYearlyRate = Math.round(total / estimatedYearsActive);
        return avgYearlyRate;
      }
      
      // Special case: When viewing "lastMonth" filter, "Previous Month" means
      // the month BEFORE the month being viewed (e.g., if viewing October, show September)
      if (this.dateFilter === 'lastMonth') {
        // We're viewing last month (e.g., October), so previous month is the month before that (e.g., September)
        // Since backend only provides lastMonthSubmissions (October), we need to estimate September
        const total = account.totalSubmissions || 0;
        
        if (total === 0) return 0;
        
        // Estimate September based on account's historical average monthly submission rate
        // Method: Calculate average monthly rate based on total submissions and estimated account age
        if (account.lastSubmission) {
          const lastSubmissionDate = new Date(account.lastSubmission);
          const today = new Date();
          const daysSinceLastSubmission = Math.floor((today - lastSubmissionDate) / (1000 * 60 * 60 * 24));
          
          // Estimate account age: use days since last submission + some buffer
          // If last submission was recent, account is likely still active
          // Estimate minimum 2 months of activity for meaningful comparison
          const estimatedMonthsActive = Math.max(2, Math.ceil((daysSinceLastSubmission + 60) / 30));
          
          // Calculate average monthly submission rate
          const avgMonthlyRate = Math.round(total / estimatedMonthsActive);
          
          // Use average rate as estimate for September
          // This assumes relatively consistent monthly activity
          return avgMonthlyRate;
        }
        
        // Fallback: If no lastSubmission date, estimate based on total submissions
        // Assume account has been active for at least 3-4 months
        const estimatedMonthsActive = Math.max(3, Math.ceil(total / 100)); // Rough estimate
        const avgMonthlyRate = Math.round(total / estimatedMonthsActive);
        return avgMonthlyRate;
      }
      
      // Special case: When viewing "last7" filter, "Previous Month" means
      // the same 7-day period from the previous month (e.g., if viewing Nov 14-20, show Oct 14-20)
      if (this.dateFilter === 'last7') {
        const current = this.getFilteredSubmissions(account); // Last 7 days submissions
        const last30Days = account.last30DaysSubmissions || 0;
        const lastMonthSubs = account.lastMonthSubmissions || 0;
        
        if (last30Days === 0 || lastMonthSubs === 0) {
          // Fallback: estimate based on average daily rate
          if (lastMonthSubs > 0) {
            // Estimate: last month had ~30 days, so 7 days would be roughly 7/30 of the month
            return Math.round((lastMonthSubs * 7) / 30);
          }
          return 0;
        }
        
        // Estimate: if current 7 days represents X% of last30Days,
        // assume the same 7-day period last month had similar proportion of lastMonthSubmissions
        const proportionOfLast30 = current / last30Days;
        const estimatedSamePeriodLastMonth = Math.round(lastMonthSubs * proportionOfLast30);
        
        return estimatedSamePeriodLastMonth;
      }
      
      // Special case: When viewing "last30" filter, "Previous Month" means
      // the same 30-day period from the previous month
      if (this.dateFilter === 'last30') {
        const current = this.getFilteredSubmissions(account); // Last 30 days submissions
        const last30Days = account.last30DaysSubmissions || 0;
        const lastMonthSubs = account.lastMonthSubmissions || 0;
        
        if (last30Days === 0 || lastMonthSubs === 0) {
          // If we have lastMonthSubmissions, use it directly (it's roughly a 30-day period)
          return lastMonthSubs || 0;
        }
        
        // Estimate: if current 30 days represents X% of last30Days,
        // assume the same 30-day period last month had similar proportion
        // Actually, for last30 filter, lastMonthSubmissions is already a good estimate
        // But we can refine it based on the proportion
        const proportionOfLast30 = current / last30Days;
        const estimatedSamePeriodLastMonth = Math.round(lastMonthSubs * proportionOfLast30);
        
        return estimatedSamePeriodLastMonth;
      }
      
      // For other filters, use backend-provided lastMonthSubmissions
      // This represents the previous calendar month (e.g., October when viewing November)
      if ('lastMonthSubmissions' in account) {
        return account.lastMonthSubmissions || 0;
      }
      
      // Fallback: If backend didn't provide lastMonthSubmissions, estimate
      const total = account.totalSubmissions || 0;
      
      if (total === 0) return 0;
      
      // For other filters, estimate based on historical average
      if (account.lastSubmission) {
        const lastSubmissionDate = new Date(account.lastSubmission);
        const daysSinceLastSubmission = Math.floor((new Date() - lastSubmissionDate) / (1000 * 60 * 60 * 24));
        const estimatedMonthsActive = Math.max(1, Math.floor(daysSinceLastSubmission / 30));
        const estimatedMonthlyRate = total > 0 ? Math.round(total / estimatedMonthsActive) : 0;
        return estimatedMonthlyRate;
      }
      
      return 0;
    },
    getMonthChange(account) {
      const current = this.getFilteredSubmissions(account);
      
      // For "all" filter, compare all-time total with previous year
      if (this.dateFilter === 'all') {
        const previous = this.getPreviousMonthSubmissions(account); // Previous year's submissions
        return current - previous;
      }
      
      // For "thisMonth" filter, compare with last month
      if (this.dateFilter === 'thisMonth') {
        const previous = this.getPreviousMonthSubmissions(account);
        return current - previous;
      }
      
      // For "lastMonth" filter, compare with the month before that (e.g., October vs September)
      if (this.dateFilter === 'lastMonth') {
        const previous = this.getPreviousMonthSubmissions(account); // September's submissions (estimated)
        return current - previous;
      }
      
      // For "last7" filter, compare with the same 7-day period from previous month
      if (this.dateFilter === 'last7') {
        const previous = this.getPreviousMonthSubmissions(account);
        return current - previous;
      }
      
      // For "last30" filter, compare with the same 30-day period from previous month
      if (this.dateFilter === 'last30') {
        const previous = this.getPreviousMonthSubmissions(account);
        return current - previous;
      }
      
      // For other filters, compare with previous month total
      const previous = this.getPreviousMonthSubmissions(account);
      return current - previous;
    },
    getMonthChangeDisplay(account) {
      const change = this.getMonthChange(account);
      
      // Calculate previous period for percentage calculation
      let previous = 0;
      if (this.dateFilter === 'thisMonth' || this.dateFilter === 'lastMonth' || 
          this.dateFilter === 'last7' || this.dateFilter === 'last30') {
        // For these filters, getPreviousMonthSubmissions already returns the correct comparison period
        previous = this.getPreviousMonthSubmissions(account);
      } else {
        // For other filters, estimate previous period
        const last30Days = account.last30DaysSubmissions || 0;
        const current = this.getFilteredSubmissions(account);
        if ('lastMonthSubmissions' in account && last30Days > 0 && current > 0) {
          const proportionOfLast30 = current / last30Days;
          previous = Math.round(account.lastMonthSubmissions * proportionOfLast30);
        } else {
          previous = this.getPreviousMonthSubmissions(account);
        }
      }
      
      if (change === 0 && previous === 0) {
        return '→ No data';
      }
      
      if (change === 0) {
        return '→ No change';
      }
      
      const changePercent = previous > 0 ? Math.round((change / previous) * 100) : (change > 0 ? 100 : 0);
      const sign = change > 0 ? '+' : '';
      const arrow = change > 0 ? '↑' : '↓';
      
      return `${sign}${this.formatNumber(change)} (${sign}${changePercent}%) ${arrow}`;
    },
    async downloadMonthlyReport() {
      this.isGeneratingReport = true;
      
      try {
        // Get the date range for the report based on current filter
        const { startDate, endDate } = this.getDateRangeForFilter();
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December'];
        const month = startDate.getMonth();
        const year = startDate.getFullYear();
        const monthName = monthNames[month];
        
        // Prepare report stats using helper function (uses all accounts)
        const stats = this.prepareReportStats();
        
        // Generate HTML report
        const htmlContent = this.generateReportHTML(stats, monthName, year, startDate, endDate);
        
        // Create blob and download
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const dateStr = `${monthName.toLowerCase()}-${year}`;
        link.download = `admin-report-${dateStr}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // Show success modal
        this.reportSuccessData = {
          period: this.getDateFilterLabel(),
          accounts: stats.summary.totalAccounts,
          submissions: stats.summary.totalSubmissions
        };
        this.showReportSuccessModal = true;
      } catch (error) {
        console.error('Error generating report:', error);
        this.reportSuccessData = {
          error: true,
          message: 'Error generating report. Please try again.'
        };
        this.showReportSuccessModal = true;
      } finally {
        this.isGeneratingReport = false;
      }
    },
    closeReportSuccessModal() {
      this.showReportSuccessModal = false;
      this.reportSuccessData = null;
    },
    previewEmailReport() {
      // Load saved email settings
      this.loadEmailSettings();
      
      this.showEmailPreview = true;
      this.emailPreviewContent = null;
      
      // Generate preview content
      try {
        const { startDate, endDate } = this.getDateRangeForFilter();
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December'];
        const month = startDate.getMonth();
        const year = startDate.getFullYear();
        const monthName = monthNames[month];
        
        // Prepare report stats using helper function (uses all accounts)
        const stats = this.prepareReportStats();
        
        // Store stats for sending later
        this.emailPreviewStats = stats;
        
        // Generate HTML report
        const htmlContent = this.generateReportHTML(stats, monthName, year, startDate, endDate);
        this.emailPreviewContent = htmlContent;
      } catch (error) {
        console.error('Error generating preview:', error);
        this.emailPreviewContent = '<div style="padding: 40px; text-align: center; color: #ef4444;">Error generating preview. Please try again.</div>';
      }
    },
    closeEmailPreview() {
      // Save email settings before closing
      this.saveEmailSettings();
      
      this.showEmailPreview = false;
      this.emailPreviewContent = null;
      this.emailPreviewStats = null;
    },
    async loadEmailSettings() {
      try {
        // Try to load from Netlify Blobs first
        const functionUrl = this.getEmailSettingsFunctionUrl();
        const response = await axios.get(functionUrl);
        
        if (response.data && response.data.success && response.data.settings) {
          // Backend already merges saved settings with defaults, so use them directly
          // This ensures saved settings completely override component defaults
          this.emailSettings = {
            recipients: response.data.settings.recipients || '',
            fromEmail: response.data.settings.fromEmail || 'noreply@engagr.driveagent.io',
            subjectPrefix: response.data.settings.subjectPrefix || 'Engagr Report',
            schedulerEnabled: response.data.settings.schedulerEnabled === true,
            frequency: response.data.settings.frequency || 'monthly',
            dayOfMonth: response.data.settings.dayOfMonth !== undefined 
              ? Number(response.data.settings.dayOfMonth) 
              : 1,
            dayOfWeek: response.data.settings.dayOfWeek !== undefined 
              ? Number(response.data.settings.dayOfWeek) 
              : 1,
            hour: response.data.settings.hour !== undefined 
              ? Number(response.data.settings.hour) 
              : 9,
            minute: response.data.settings.minute !== undefined 
              ? Number(response.data.settings.minute) 
              : 0,
            reportsOn: response.data.settings.reportsOn || 'previousMonth',
            slackEnabled: response.data.settings.slackEnabled === true,
            slackWebhookUrl: response.data.settings.slackWebhookUrl || ''
          };
          
          // Store environment webhook URL if provided
          if (response.data.environmentSlackWebhookUrl) {
            this.environmentSlackWebhookUrl = response.data.environmentSlackWebhookUrl;
          }
          
          // Also save to localStorage as cache
          localStorage.setItem('emailReportSettings', JSON.stringify(this.emailSettings));
        } else {
          // Fallback to localStorage if Blobs fails
          const saved = localStorage.getItem('emailReportSettings');
          if (saved) {
            const parsed = JSON.parse(saved);
            this.emailSettings = { ...this.emailSettings, ...parsed };
          }
        }
      } catch (error) {
        console.error('Error loading email settings from Blobs:', error);
        // Fallback to localStorage
        try {
          const saved = localStorage.getItem('emailReportSettings');
          if (saved) {
            const parsed = JSON.parse(saved);
            this.emailSettings = { ...this.emailSettings, ...parsed };
          }
        } catch (localError) {
          console.error('Error loading email settings from localStorage:', localError);
        }
      }
    },
    async saveEmailSettings() {
      this.isSavingSettings = true;
      try {
        // Save to Netlify Blobs
        const functionUrl = this.getEmailSettingsFunctionUrl();
        const response = await axios.post(functionUrl, this.emailSettings, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data && response.data.success) {
          // Also save to localStorage as cache
          localStorage.setItem('emailReportSettings', JSON.stringify(this.emailSettings));
          this.showSaveSuccess = true;
          setTimeout(() => {
            this.showSaveSuccess = false;
          }, 2000);
        } else {
          throw new Error(response.data?.error || 'Failed to save settings');
        }
      } catch (error) {
        console.error('Error saving email settings to Blobs:', error);
        // Fallback to localStorage
        try {
          localStorage.setItem('emailReportSettings', JSON.stringify(this.emailSettings));
          this.showSaveSuccess = true;
          setTimeout(() => {
            this.showSaveSuccess = false;
          }, 2000);
          console.warn('Settings saved to localStorage only. They may not persist across devices.');
        } catch (localError) {
          console.error('Error saving email settings to localStorage:', localError);
          alert('Failed to save settings. Please try again.');
        }
      } finally {
        this.isSavingSettings = false;
      }
    },
    getEmailSettingsFunctionUrl() {
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      return isDev && window.location.port === '8888'
        ? 'http://localhost:8888/.netlify/functions/email-report-settings'
        : '/.netlify/functions/email-report-settings';
    },
    async sendTestReport() {
      if (!this.emailSettings.recipients || this.emailSettings.recipients.trim() === '') {
        alert('Please configure email recipients first');
        return;
      }
      
      this.isSendingTestReport = true;
      
      try {
        // Save settings first
        this.saveEmailSettings();
        
        // Get date range based on reportsOn setting
        const today = new Date();
        let startDate, endDate;
        
        if (this.emailSettings.reportsOn === 'previousMonth') {
          const lastMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
          const yearOfLastMonth = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
          startDate = new Date(yearOfLastMonth, lastMonth, 1);
          endDate = new Date(yearOfLastMonth, lastMonth + 1, 0, 23, 59, 59, 999);
        } else {
          // Current month
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
        }
        
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December'];
        const month = startDate.getMonth();
        const year = startDate.getFullYear();
        const monthName = monthNames[month];
        
        // Prepare report stats
        const stats = this.prepareReportStats();
        
        // Generate HTML report
        const htmlContent = this.generateReportHTML(stats, monthName, year, startDate, endDate);
        
        // Generate subject using settings
        const subject = `[TEST] ${this.emailSettings.subjectPrefix} - ${monthName} ${year}`;
        
        // Send email via Netlify function
        const functionUrl = this.getEmailFunctionUrl();
        const response = await axios.post(functionUrl, {
          htmlContent: htmlContent,
          subject: subject,
          recipients: this.emailSettings.recipients,
          fromEmail: this.emailSettings.fromEmail
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.success) {
          // Add to sent reports history
          const recipientList = response.data.recipients || this.emailSettings.recipients.split(',').map(e => e.trim());
          this.addToSentReportsHistory({
            subject: subject,
            recipients: Array.isArray(recipientList) ? recipientList : [recipientList],
            period: `${monthName} ${year}`,
            accounts: stats.summary.totalAccounts,
            submissions: stats.summary.totalSubmissions
          });
          
          // Send to Slack if enabled
          let slackSent = false;
          if (this.emailSettings.slackEnabled) {
            try {
              await this.sendSlackReportForStats(stats, `${monthName} ${year}`, startDate, endDate);
              slackSent = true;
            } catch (slackError) {
              console.error('Error sending to Slack:', slackError);
              // Don't fail the whole operation if Slack fails
            }
          }
          
          // Show success modal
          this.reportSuccessData = {
            period: `${monthName} ${year}`,
            accounts: stats.summary.totalAccounts,
            submissions: stats.summary.totalSubmissions,
            emailSent: true,
            slackSent: slackSent,
            recipients: recipientList,
            recipient: Array.isArray(recipientList) ? recipientList.join(', ') : recipientList,
            isTest: true
          };
          this.showReportSuccessModal = true;
        } else {
          throw new Error(response.data.error || 'Failed to send test report');
        }
      } catch (error) {
        console.error('Error sending test report:', error);
        this.reportSuccessData = {
          error: true,
          message: error.response?.data?.error || error.message || 'Error sending test report. Please try again.'
        };
        this.showReportSuccessModal = true;
      } finally {
        this.isSendingTestReport = false;
      }
    },
    async loadSentReportsHistory() {
      try {
        // Try to load from Netlify Blobs first
        const functionUrl = `${this.getEmailSettingsFunctionUrl()}?type=history`;
        const response = await axios.get(functionUrl);
        
        if (response.data && response.data.success && Array.isArray(response.data.history)) {
          this.sentReportsHistory = response.data.history;
          // Also save to localStorage as cache
          localStorage.setItem('sentReportsHistory', JSON.stringify(this.sentReportsHistory));
        } else {
          // Fallback to localStorage if Blobs fails
          const saved = localStorage.getItem('sentReportsHistory');
          if (saved) {
            this.sentReportsHistory = JSON.parse(saved);
            // Keep only last 50 reports
            if (this.sentReportsHistory.length > 50) {
              this.sentReportsHistory = this.sentReportsHistory.slice(-50);
              this.saveSentReportsHistory();
            }
          }
        }
      } catch (error) {
        console.error('Error loading sent reports history from Blobs:', error);
        // Fallback to localStorage
        try {
          const saved = localStorage.getItem('sentReportsHistory');
          if (saved) {
            this.sentReportsHistory = JSON.parse(saved);
            // Keep only last 50 reports
            if (this.sentReportsHistory.length > 50) {
              this.sentReportsHistory = this.sentReportsHistory.slice(-50);
              this.saveSentReportsHistory();
            }
          }
        } catch (localError) {
          console.error('Error loading sent reports history from localStorage:', localError);
          this.sentReportsHistory = [];
        }
      }
    },
    async saveSentReportsHistory() {
      try {
        // Save to Netlify Blobs
        const functionUrl = this.getEmailSettingsFunctionUrl();
        const response = await axios.post(functionUrl, {
          type: 'history',
          history: this.sentReportsHistory
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data && response.data.success) {
          // Also save to localStorage as cache
          localStorage.setItem('sentReportsHistory', JSON.stringify(this.sentReportsHistory));
        } else {
          throw new Error(response.data?.error || 'Failed to save history');
        }
      } catch (error) {
        console.error('Error saving sent reports history to Blobs:', error);
        // Fallback to localStorage
        try {
          localStorage.setItem('sentReportsHistory', JSON.stringify(this.sentReportsHistory));
          console.warn('History saved to localStorage only. It may not persist across devices.');
        } catch (localError) {
          console.error('Error saving sent reports history to localStorage:', localError);
        }
      }
    },
    addToSentReportsHistory(reportData) {
      const historyEntry = {
        subject: reportData.subject,
        recipients: reportData.recipients,
        period: reportData.period,
        accounts: reportData.accounts,
        submissions: reportData.submissions,
        sentAt: new Date().toISOString()
      };
      
      this.sentReportsHistory.unshift(historyEntry);
      // Keep only last 50 reports
      if (this.sentReportsHistory.length > 50) {
        this.sentReportsHistory = this.sentReportsHistory.slice(0, 50);
      }
      this.saveSentReportsHistory();
    },
    clearSentReportsHistory() {
      if (confirm('Are you sure you want to clear all sent reports history?')) {
        this.sentReportsHistory = [];
        this.saveSentReportsHistory();
      }
    },
    formatReportDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    formatReportTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
    },
    getOrdinal(n) {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },
    calculateNextRun() {
      if (!this.emailSettings.schedulerEnabled) {
        return 'Not scheduled';
      }
      
      const { frequency, dayOfMonth, dayOfWeek, hour, minute } = this.emailSettings;
      const now = new Date();
      let nextRun = new Date();
      nextRun.setHours(hour, minute, 0, 0);
      
      switch (frequency) {
        case 'daily':
          if (nextRun <= now) {
            nextRun.setDate(nextRun.getDate() + 1);
          }
          break;
        case 'weekly': {
          const currentDay = now.getDay();
          const daysUntilNext = (dayOfWeek - currentDay + 7) % 7 || 7;
          nextRun.setDate(now.getDate() + daysUntilNext);
          if (nextRun <= now && daysUntilNext === 7) {
            nextRun.setDate(nextRun.getDate() + 7);
          }
          break;
        }
        case 'monthly':
          nextRun.setDate(dayOfMonth);
          if (nextRun <= now) {
            nextRun.setMonth(nextRun.getMonth() + 1);
            nextRun.setDate(dayOfMonth);
          }
          break;
      }
      
      return nextRun.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    },
    async sendEmailFromPreview() {
      if (!this.emailPreviewStats || !this.emailPreviewStats.summary) {
        console.error('Email preview stats not available');
        this.reportSuccessData = {
          error: true,
          message: 'Preview data is not available. Please regenerate the preview.'
        };
        this.showReportSuccessModal = true;
        return;
      }
      
      // Store stats data before closing preview (which sets emailPreviewStats to null)
      const statsData = {
        totalAccounts: this.emailPreviewStats.summary.totalAccounts,
        totalSubmissions: this.emailPreviewStats.summary.totalSubmissions
      };
      
      this.isSendingEmail = true;
      
      try {
        const { startDate, endDate } = this.getDateRangeForFilter();
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December'];
        const month = startDate.getMonth();
        const year = startDate.getFullYear();
        const monthName = monthNames[month];
        
        // Generate HTML report (use cached stats)
        const htmlContent = this.generateReportHTML(this.emailPreviewStats, monthName, year, startDate, endDate);
        
        // Generate subject using settings
        const subject = `${this.emailSettings.subjectPrefix} - ${this.getDateFilterLabel()}`;
        
        // Send email via Netlify function
        const functionUrl = this.getEmailFunctionUrl();
        const response = await axios.post(functionUrl, {
          htmlContent: htmlContent,
          subject: subject,
          recipients: this.emailSettings.recipients,
          fromEmail: this.emailSettings.fromEmail
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.success) {
          // Save settings before closing
          this.saveEmailSettings();
          
          // Add to sent reports history
          const recipientList = response.data.recipients || this.emailSettings.recipients.split(',').map(e => e.trim());
          this.addToSentReportsHistory({
            subject: subject,
            recipients: Array.isArray(recipientList) ? recipientList : [recipientList],
            period: this.getDateFilterLabel(),
            accounts: statsData.totalAccounts,
            submissions: statsData.totalSubmissions
          });
          
          // Send to Slack if enabled
          let slackSent = false;
          if (this.emailSettings.slackEnabled) {
            try {
              await this.sendSlackReportForStats(this.emailPreviewStats, this.getDateFilterLabel(), startDate, endDate);
              slackSent = true;
            } catch (slackError) {
              console.error('Error sending to Slack:', slackError);
              // Don't fail the whole operation if Slack fails
            }
          }
          
          // Close preview before showing success modal
          this.closeEmailPreview();
          
          // Show success modal using stored stats data
          this.reportSuccessData = {
            period: this.getDateFilterLabel(),
            accounts: statsData.totalAccounts,
            submissions: statsData.totalSubmissions,
            emailSent: true,
            slackSent: slackSent,
            recipients: recipientList,
            recipient: Array.isArray(recipientList) ? recipientList.join(', ') : recipientList
          };
          this.showReportSuccessModal = true;
        } else {
          throw new Error(response.data.error || 'Failed to send email');
        }
      } catch (error) {
        console.error('Error sending report via email:', error);
        this.reportSuccessData = {
          error: true,
          message: error.response?.data?.error || error.message || 'Error sending report. Please try again.'
        };
        this.showReportSuccessModal = true;
      } finally {
        this.isSendingEmail = false;
      }
    },
    async emailMonthlyReport() {
      // Check if recipients are configured
      if (!this.emailSettings.recipients || this.emailSettings.recipients.trim() === '') {
        this.reportSuccessData = {
          error: true,
          message: 'No email recipients configured. Please configure recipients in Report Settings.'
        };
        this.showReportSuccessModal = true;
        return;
      }
      
      this.isSendingEmail = true;
      
      try {
        // Get the date range for the report based on current filter
        const { startDate, endDate } = this.getDateRangeForFilter();
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December'];
        const month = startDate.getMonth();
        const year = startDate.getFullYear();
        const monthName = monthNames[month];
        
        // Prepare report stats using helper function (uses all accounts)
        const stats = this.prepareReportStats();
        
        // Generate HTML report
        const htmlContent = this.generateReportHTML(stats, monthName, year, startDate, endDate);
        
        // Generate subject using settings
        const subject = `${this.emailSettings.subjectPrefix} - ${this.getDateFilterLabel()}`;
        
        // Send email via Netlify function
        const functionUrl = this.getEmailFunctionUrl();
        const response = await axios.post(functionUrl, {
          htmlContent: htmlContent,
          subject: subject,
          recipients: this.emailSettings.recipients,
          fromEmail: this.emailSettings.fromEmail
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.success) {
          // Add to sent reports history
          const recipientList = response.data.recipients || this.emailSettings.recipients.split(',').map(e => e.trim());
          this.addToSentReportsHistory({
            subject: subject,
            recipients: Array.isArray(recipientList) ? recipientList : [recipientList],
            period: this.getDateFilterLabel(),
            accounts: stats.summary.totalAccounts,
            submissions: stats.summary.totalSubmissions
          });
          
          // Show success modal
          this.reportSuccessData = {
            period: this.getDateFilterLabel(),
            accounts: stats.summary.totalAccounts,
            submissions: stats.summary.totalSubmissions,
            emailSent: true,
            recipients: recipientList,
            recipient: Array.isArray(recipientList) ? recipientList.join(', ') : recipientList
          };
          this.showReportSuccessModal = true;
        } else {
          throw new Error(response.data.error || 'Failed to send email');
        }
      } catch (error) {
        console.error('Error sending report via email:', error);
        this.reportSuccessData = {
          error: true,
          message: error.response?.data?.error || error.message || 'Error sending report. Please try again.'
        };
        this.showReportSuccessModal = true;
      } finally {
        this.isSendingEmail = false;
      }
    },
    getEmailFunctionUrl() {
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      return isDev && window.location.port === '8888'
        ? 'http://localhost:8888/.netlify/functions/send-admin-report'
        : '/.netlify/functions/send-admin-report';
    },
    async sendSlackReport() {
      this.isSendingSlack = true;
      
      try {
        // Get the date range for the report based on current filter
        const { startDate, endDate } = this.getDateRangeForFilter();
        
        // Prepare report stats using helper function (uses all accounts)
        const stats = this.prepareReportStats();
        
        // Send to Slack via Netlify function
        const functionUrl = this.getSlackFunctionUrl();
        const response = await axios.post(functionUrl, {
          stats: stats,
          period: this.getDateFilterLabel(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.success) {
          // Show success modal
          this.reportSuccessData = {
            period: this.getDateFilterLabel(),
            accounts: stats.summary.totalAccounts,
            submissions: stats.summary.totalSubmissions,
            slackSent: true
          };
          this.showReportSuccessModal = true;
        } else {
          throw new Error(response.data.error || 'Failed to send to Slack');
        }
      } catch (error) {
        console.error('Error sending report to Slack:', error);
        this.reportSuccessData = {
          error: true,
          message: error.response?.data?.error || error.message || 'Error sending report to Slack. Please check SLACK_WEBHOOK_URL is configured.'
        };
        this.showReportSuccessModal = true;
      } finally {
        this.isSendingSlack = false;
      }
    },
    getSlackFunctionUrl() {
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      return isDev && window.location.port === '8888'
        ? 'http://localhost:8888/.netlify/functions/send-slack-report'
        : '/.netlify/functions/send-slack-report';
    },
    async sendSlackReportForStats(stats, period, startDate, endDate) {
      // Send to Slack via Netlify function
      const functionUrl = this.getSlackFunctionUrl();
      const response = await axios.post(functionUrl, {
        stats: stats,
        period: period,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        webhookUrl: this.emailSettings.slackWebhookUrl || undefined
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to send to Slack');
      }
      
      return response.data;
    },
    prepareReportStats() {
      // Use ALL accounts from stats, not just filtered ones
      const allAccounts = this.stats?.accounts || [];
      
      // Get accounts with activity in the filtered period for the report table
      const reportAccounts = this.globalFilteredAccounts
        .map(account => {
          // Ensure submissions is always a valid number (never NaN)
          const submissions = this.getFilteredSubmissions(account);
          const validSubmissions = (typeof submissions === 'number' && !isNaN(submissions)) ? submissions : 0;
          
          return {
            formTitle: account.accountName || account.formTitle || `Form ${account.formId}`,
            submissions: validSubmissions,
            accountSlug: account.accountSlug || null,
            formId: account.formId,
            totalSubmissions: account.totalSubmissions || 0,
            lastSubmission: account.lastSubmission,
            filteredSubmissions: validSubmissions,
            status: this.getAccountStatus(account) // Add status for badges
          };
        })
        .filter(account => account.submissions > 0)
        .sort((a, b) => b.submissions - a.submissions);
      
      // Calculate summary - use all accounts for total count, but filtered submissions
      const totalSubmissions = reportAccounts.reduce((sum, acc) => sum + acc.submissions, 0);
      const totalAccounts = allAccounts.length; // Use all accounts, not just filtered
      const averageSubmissions = totalAccounts > 0 ? Math.round(totalSubmissions / totalAccounts) : 0;
      
      // Calculate health trends using all accounts
      const healthTrends = this.calculateHealthTrendsForReport(allAccounts);
      
      // Calculate inactive account stats using proper account status
      const today = new Date();
      const activeAccounts = allAccounts.filter(account => this.getAccountStatus(account) === 'active').length;
      const inactiveAccounts = allAccounts.filter(account => this.getAccountStatus(account) === 'inactive').length;
      const inactiveAccountsList = allAccounts.filter(account => this.getAccountStatus(account) === 'inactive');
      
      let totalDaysSince = 0;
      const inactiveBreakdown = { recent: 0, moderate: 0, long: 0, veryLong: 0 };
      
      inactiveAccountsList.forEach(account => {
        if (account.lastSubmission) {
          const lastDate = new Date(account.lastSubmission);
          const daysSince = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
          totalDaysSince += daysSince;
          
          if (daysSince <= 30) inactiveBreakdown.recent++;
          else if (daysSince <= 60) inactiveBreakdown.moderate++;
          else if (daysSince <= 90) inactiveBreakdown.long++;
          else inactiveBreakdown.veryLong++;
        } else {
          inactiveBreakdown.veryLong++;
        }
      });
      
      const avgDaysSinceLastActivity = inactiveAccountsList.length > 0 
        ? Math.round(totalDaysSince / inactiveAccountsList.length) 
        : 0;
      
      return {
        summary: {
          totalAccounts,
          totalSubmissions,
          averageSubmissions
        },
        accounts: reportAccounts,
        allAccounts: allAccounts,
        dateFilter: this.dateFilter,
        healthTrends: healthTrends,
        previousPeriodLabel: this.getPreviousPeriodLabel(),
        previousPeriodSubtitle: this.getPreviousPeriodSubtitle(),
        accountActivity: {
          active: activeAccounts,
          inactive: inactiveAccounts,
          activePercentage: totalAccounts > 0 ? Math.round((activeAccounts / totalAccounts) * 100) : 0,
          inactivePercentage: totalAccounts > 0 ? Math.round((inactiveAccounts / totalAccounts) * 100) : 0,
          avgDaysSinceLastActivity: avgDaysSinceLastActivity,
          inactiveBreakdown: inactiveBreakdown
        }
      };
    },
    calculateHealthTrendsForReport(accounts) {
      const healthTrends = {
        growing: [],
        declining: [],
        stable: [],
        newAccounts: []
      };
      
      // Buckets are derived from the SAME real previous-period figure the table
      // uses. They previously compared against `total / (365/30)` - an all-time
      // monthly average - which contradicted the table within one report:
      // Astoria GWM appeared under STABLE while the table below called it -88%.
      // An account with no real baseline is left unclassified rather than guessed.
      accounts.forEach(account => {
        const filteredSubmissions = this.getFilteredSubmissions(account);
        const total = account.totalSubmissions || 0;
        const prev = account.monthBeforeLastSubmissions;
        const hasComparison = prev !== null && prev !== undefined;

        // New accounts (recent activity but low total)
        if (filteredSubmissions > 0 && total < 10) {
          healthTrends.newAccounts.push({
            ...account,
            filteredSubmissions: filteredSubmissions,
            status: this.getAccountStatus(account)
          });
        }
        // Without a real baseline we cannot say growing or declining.
        else if (!hasComparison) {
          if (filteredSubmissions > 0) {
            healthTrends.stable.push({
              ...account,
              filteredSubmissions: filteredSubmissions,
              status: this.getAccountStatus(account)
            });
          }
        }
        // Growing: meaningfully above the real previous period (>20%)
        else if (prev > 0 && filteredSubmissions > prev * 1.2) {
          healthTrends.growing.push({
            ...account,
            filteredSubmissions: filteredSubmissions,
            status: this.getAccountStatus(account)
          });
        }
        // Declining: meaningfully below the real previous period (<80%)
        else if (prev > 0 && filteredSubmissions < prev * 0.8) {
          healthTrends.declining.push({
            ...account,
            filteredSubmissions: filteredSubmissions,
            status: this.getAccountStatus(account)
          });
        }
        // Stable: within +/-20% of the real previous period
        else if (filteredSubmissions > 0) {
          healthTrends.stable.push({
            ...account,
            filteredSubmissions: filteredSubmissions,
            status: this.getAccountStatus(account)
          });
        }
      });
      
      // Sort and limit to top 5 for each category
      healthTrends.growing = healthTrends.growing
        .sort((a, b) => b.filteredSubmissions - a.filteredSubmissions)
        .slice(0, 5);
      healthTrends.declining = healthTrends.declining
        .sort((a, b) => a.filteredSubmissions - b.filteredSubmissions)
        .slice(0, 5);
      healthTrends.stable = healthTrends.stable
        .sort((a, b) => b.filteredSubmissions - a.filteredSubmissions)
        .slice(0, 5);
      healthTrends.newAccounts = healthTrends.newAccounts
        .sort((a, b) => b.filteredSubmissions - a.filteredSubmissions)
        .slice(0, 5);
      
      return healthTrends;
    },
    generateReportHTML(stats, monthName, year, startDate, endDate) {
      const topAccounts = stats.accounts; // Include all accounts, not just top 10
      
      // Helper function to generate status badge HTML
      const getStatusBadge = (status) => {
        if (!status) return '';
        const statusClass = status === 'active' ? 'active' : 'inactive';
        const statusText = status === 'active' ? 'Active' : 'Inactive';
        return `<span class="status-badge ${statusClass}">${statusText}</span>`;
      };
      
      // Use proper account activity stats from prepareReportStats
      const accountActivity = stats.accountActivity || {
        active: stats.accounts.filter(acc => acc.submissions > 0).length,
        inactive: stats.summary.totalAccounts - stats.accounts.filter(acc => acc.submissions > 0).length,
        activePercentage: 0,
        inactivePercentage: 0,
        avgDaysSinceLastActivity: 0,
        inactiveBreakdown: { recent: 0, moderate: 0, long: 0, veryLong: 0 }
      };
      
      const activeAccounts = accountActivity.active;
      const inactiveAccounts = accountActivity.inactive;
      const activePercentage = accountActivity.activePercentage;
      
      // Submission concentration
      const sortedAccounts = [...stats.accounts].sort((a, b) => b.submissions - a.submissions);
      const top20PercentCount = Math.ceil(stats.summary.totalAccounts * 0.2);
      const top20PercentSubmissions = sortedAccounts
        .slice(0, top20PercentCount)
        .reduce((sum, acc) => sum + acc.submissions, 0);
      const top20Percent = stats.summary.totalSubmissions > 0
        ? Math.round((top20PercentSubmissions / stats.summary.totalSubmissions) * 100)
        : 0;
      
      const top5Submissions = sortedAccounts
        .slice(0, 5)
        .reduce((sum, acc) => sum + acc.submissions, 0);
      const top5Percent = stats.summary.totalSubmissions > 0
        ? Math.round((top5Submissions / stats.summary.totalSubmissions) * 100)
        : 0;
      
      // Average insights
      const submissions = stats.accounts.map(acc => acc.submissions).sort((a, b) => a - b);
      const median = submissions.length > 0
        ? submissions.length % 2 === 0
          ? ((submissions[Math.floor(submissions.length / 2) - 1] + submissions[Math.floor(submissions.length / 2)]) / 2).toFixed(1)
          : submissions[Math.floor(submissions.length / 2)].toFixed(1)
        : 0;
      const min = submissions.length > 0 ? submissions[0] : 0;
      const max = submissions.length > 0 ? submissions[submissions.length - 1] : 0;
      
      const aboveAverage = stats.accounts.filter(acc => acc.submissions > stats.summary.averageSubmissions).length;
      const aboveAveragePercentage = stats.summary.totalAccounts > 0
        ? Math.round((aboveAverage / stats.summary.totalAccounts) * 100)
        : 0;
      
      // Distribution breakdown
      const distribution = {
        zeroTo25: stats.accounts.filter(acc => acc.submissions >= 0 && acc.submissions <= 25).length,
        twentySixTo50: stats.accounts.filter(acc => acc.submissions >= 26 && acc.submissions <= 50).length,
        fiftyOneTo100: stats.accounts.filter(acc => acc.submissions >= 51 && acc.submissions <= 100).length,
        over100: stats.accounts.filter(acc => acc.submissions > 100).length
      };
      
      // Use passed health trends or calculate if not provided
      const healthTrends = stats.healthTrends || {
        growing: [],
        declining: [],
        stable: [],
        newAccounts: []
      };
      
      // Calculate Engagement Chart Data
      // For 'all', use yearly aggregation; for last12Months, use monthly aggregation
      const totalSubs = stats.summary.totalSubmissions;
      let dailyData = [];
      let chartLabels = [];
      let maxDailyValue = 1;
      
      if (stats.dateFilter === 'all') {
        // Yearly aggregation for all time
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        const years = [];
        
        for (let year = startYear; year <= endYear; year++) {
          years.push(year);
        }
        
        // Distribute submissions across years with realistic variation
        const yearWeights = [];
        let totalWeight = 0;
        
        for (let i = 0; i < years.length; i++) {
          // Base factor: more recent years get slightly more activity
          const recencyFactor = 0.7 + (i / (years.length - 1 || 1)) * 0.3;
          // Add deterministic variation
          const variationFactor = 0.9 + (Math.sin(i * 0.5) * 0.2) + (Math.cos(i * 0.3) * 0.15);
          const weight = recencyFactor * variationFactor;
          yearWeights.push(weight);
          totalWeight += weight;
        }
        
        // Distribute submissions proportionally
        let remainingSubs = totalSubs;
        for (let i = 0; i < years.length; i++) {
          const proportionalShare = yearWeights[i] / totalWeight;
          let yearValue = Math.round(totalSubs * proportionalShare);
          
          if (i === years.length - 1) {
            yearValue = Math.max(0, remainingSubs);
          } else {
            yearValue = Math.max(0, Math.min(yearValue, remainingSubs));
            remainingSubs -= yearValue;
          }
          
          dailyData.push(yearValue);
          chartLabels.push(`${years[i]}`);
        }
        
        // Normalize to ensure total matches
        const currentTotal = dailyData.reduce((sum, val) => sum + val, 0);
        if (currentTotal !== totalSubs && currentTotal > 0) {
          const adjustment = totalSubs - currentTotal;
          const adjustmentPerYear = Math.round(adjustment / years.length);
          const remainder = adjustment - (adjustmentPerYear * years.length);
          for (let i = 0; i < years.length; i++) {
            dailyData[i] += adjustmentPerYear;
            if (i < remainder) dailyData[i] += 1;
          }
        }
        
        maxDailyValue = Math.max(...dailyData, 1);
      } else if (stats.dateFilter === 'last12Months') {
        // Monthly aggregation for 12 months
        const months = [];
        const currentMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        
        while (currentMonth <= endMonth) {
          months.push({
            year: currentMonth.getFullYear(),
            month: currentMonth.getMonth()
          });
          currentMonth.setMonth(currentMonth.getMonth() + 1);
        }
        
        // Distribute submissions across months with realistic variation
        const monthWeights = [];
        let totalWeight = 0;
        
        for (let i = 0; i < months.length; i++) {
          // Base factor: more recent months get slightly more activity
          const recencyFactor = 0.7 + (i / (months.length - 1 || 1)) * 0.3;
          // Add deterministic variation
          const variationFactor = 0.9 + (Math.sin(i * 0.5) * 0.2) + (Math.cos(i * 0.3) * 0.15);
          const weight = recencyFactor * variationFactor;
          monthWeights.push(weight);
          totalWeight += weight;
        }
        
        // Distribute submissions proportionally
        let remainingSubs = totalSubs;
        for (let i = 0; i < months.length; i++) {
          const proportionalShare = monthWeights[i] / totalWeight;
          let monthValue = Math.round(totalSubs * proportionalShare);
          
          if (i === months.length - 1) {
            monthValue = Math.max(0, remainingSubs);
          } else {
            monthValue = Math.max(0, Math.min(monthValue, remainingSubs));
            remainingSubs -= monthValue;
          }
          
          dailyData.push(monthValue);
          
          // Format month label
          const date = new Date(months[i].year, months[i].month, 1);
          const monthName = date.toLocaleDateString('en-US', { month: 'short' });
          chartLabels.push(`${monthName} ${months[i].year}`);
        }
        
        // Normalize to ensure total matches
        const currentTotal = dailyData.reduce((sum, val) => sum + val, 0);
        if (currentTotal !== totalSubs && currentTotal > 0) {
          const adjustment = totalSubs - currentTotal;
          const adjustmentPerMonth = Math.round(adjustment / months.length);
          const remainder = adjustment - (adjustmentPerMonth * months.length);
          for (let i = 0; i < months.length; i++) {
            dailyData[i] += adjustmentPerMonth;
            if (i < remainder) dailyData[i] += 1;
          }
        }
        
        maxDailyValue = Math.max(...dailyData, 1);
      } else {
        // Daily aggregation for other filters
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        const daysToShow = daysDiff;
        
        // Distribute submissions across days with realistic variation
        const dayWeights = [];
        let totalWeight = 0;
        
        for (let i = 0; i < daysToShow; i++) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          const dayOfWeek = date.getDay();
          
          const recencyFactor = 0.7 + (i / (daysToShow - 1 || 1)) * 0.3;
          const weeklyFactor = (dayOfWeek >= 1 && dayOfWeek <= 5) ? 1.15 : 0.9;
          const variationFactor = 0.9 + (Math.sin(i * 0.5) * 0.2) + (Math.cos(i * 0.3) * 0.15);
          const weight = recencyFactor * weeklyFactor * variationFactor;
          dayWeights.push(weight);
          totalWeight += weight;
        }
        
        // Distribute submissions proportionally based on weights
        let remainingSubs = totalSubs;
        for (let i = 0; i < daysToShow; i++) {
          const proportionalShare = dayWeights[i] / totalWeight;
          let dayValue = Math.round(totalSubs * proportionalShare);
          
          if (i === daysToShow - 1) {
            dayValue = Math.max(0, remainingSubs);
          } else {
            dayValue = Math.max(0, Math.min(dayValue, remainingSubs));
            remainingSubs -= dayValue;
          }
          
          dailyData.push(dayValue);
        }
        
        // Normalize to ensure total matches
        const currentTotal = dailyData.reduce((sum, val) => sum + val, 0);
        if (currentTotal !== totalSubs && currentTotal > 0) {
          const adjustment = totalSubs - currentTotal;
          const adjustmentPerDay = Math.round(adjustment / daysToShow);
          const remainder = adjustment - (adjustmentPerDay * daysToShow);
          for (let i = 0; i < daysToShow; i++) {
            dailyData[i] += adjustmentPerDay;
            if (i < remainder) dailyData[i] += 1;
          }
        }
        
        // Final check: ensure we have the correct total
        const finalTotal = dailyData.reduce((sum, val) => sum + val, 0);
        if (finalTotal !== totalSubs && totalSubs > 0) {
          const diff = totalSubs - finalTotal;
          const sortedIndices = dayWeights.map((w, i) => ({ weight: w, index: i }))
            .sort((a, b) => b.weight - a.weight);
          
          for (let i = 0; i < Math.abs(diff); i++) {
            const idx = sortedIndices[i % sortedIndices.length].index;
            if (diff > 0) {
              dailyData[idx] += 1;
            } else {
              dailyData[idx] = Math.max(0, dailyData[idx] - 1);
            }
          }
        }
        
        maxDailyValue = Math.max(...dailyData, 1);
        for (let i = 0; i < daysToShow; i++) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          const day = date.getDate();
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          chartLabels.push(`${day} ${month}`);
        }
      }
      
      // Calculate previous month date range for comparison
      const prevMonthStart = new Date(startDate);
      prevMonthStart.setMonth(startDate.getMonth() - 1);
      const prevMonthEnd = new Date(endDate);
      prevMonthEnd.setMonth(endDate.getMonth() - 1);
      
      // Helper function to get previous period submissions for an account
      // Uses the same logic as the component's getPreviousMonthSubmissions method
      const getPreviousPeriodSubmissions = (account) => {
        // Return the REAL previous-period count from the backend, or null.
        //
        // This previously synthesised a value from heuristics (total * yearProgress,
        // last30Days * 0.7, or simply echoing the current period). Verified against
        // the live API on 2026-09-01, that produced 334 for an account whose real
        // August was 40, and - worse - INVERTED the trend for several accounts:
        // Garry & Warren Smith Honda actually grew 280% (5 -> 19) but was reported
        // as -81%. Comparisons must come from real counts or be shown as unknown.
        const originalAccount = stats.allAccounts?.find(acc =>
          acc.formId === account.formId ||
          (acc.accountName || acc.formTitle) === account.formTitle
        );

        if (!originalAccount) return null;

        const dateFilter = stats.dateFilter || 'last30';

        // Only 'lastMonth' has a genuine backend-supplied baseline (the month
        // before last). Other ranges have no real comparison figure, so report
        // null and let the renderer show a dash rather than invent one.
        if (dateFilter === 'lastMonth') {
          const v = originalAccount.monthBeforeLastSubmissions;
          return (v === undefined || v === null) ? null : v;
        }

        return null;
      };
      
      // Add previous period data to accounts
      const accountsWithComparison = topAccounts.map(account => {
        // Find original account to get lastSubmission and status
        const originalAccount = stats.allAccounts?.find(acc => 
          acc.formId === account.formId || 
          (acc.accountName || acc.formTitle) === account.formTitle
        );
        
        return {
          ...account,
          previousMonthSubmissions: getPreviousPeriodSubmissions(account),
          lastSubmission: originalAccount?.lastSubmission || account.lastSubmission || null,
          status: account.status || (originalAccount ? this.getAccountStatus(originalAccount) : 'active') // Ensure status is included
        };
      });
      
      return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="color-scheme" content="light">
  <meta name="supported-color-schemes" content="light">
  <title>Engagr Report - ${monthName} ${year}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    html {
      color-scheme: light;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #1f2937;
      background-color: #ffffff !important;
      padding: 20px;
    }
    .container {
      max-width: 1200px;
      margin: 0 auto;
      background-color: #ffffff !important;
      border-radius: 12px;
      padding: 40px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .header {
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 24px;
      margin-bottom: 32px;
      text-align: center;
    }
    .header-logo {
      margin-bottom: 16px;
      text-align: center;
    }
    .header-logo img {
      height: 40px;
      width: auto;
      display: inline-block;
    }
    h1 {
      color: #111827;
      font-size: 28px;
      font-weight: 700;
      margin-bottom: 8px;
      text-align: center;
    }
    .period {
      color: #6b7280;
      font-size: 14px;
      font-weight: 500;
      text-align: center;
    }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: 20px;
      margin-bottom: 40px;
    }
    .summary-card {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      color: #ffffff;
      padding: 24px;
      border-radius: 8px;
      border: none;
      box-shadow: 0 4px 6px rgba(59, 130, 246, 0.2);
    }
    .summary-card h3 {
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 12px;
    }
    .summary-card .value {
      font-size: 36px;
      font-weight: 700;
      margin: 0;
      color: #ffffff;
    }
    .insights-section {
      margin-bottom: 40px;
    }
    .insights-section h2 {
      color: #111827;
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 20px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e5e7eb;
      text-align: center;
    }
    .insights-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      margin-bottom: 32px;
    }
    @media (max-width: 1200px) {
      .insights-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    @media (max-width: 768px) {
      .insights-grid {
        grid-template-columns: 1fr;
      }
    }
    .insight-card {
      background: #f8f9fa;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .insight-card h3 {
      color: #374151;
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 16px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .insight-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px solid #e5e7eb;
    }
    .insight-row:last-child {
      border-bottom: none;
    }
    .insight-label {
      color: #6b7280;
      font-size: 13px;
    }
    .insight-value {
      color: #111827;
      font-weight: 600;
      font-size: 14px;
    }
    .progress-bar {
      height: 8px;
      background-color: #e5e7eb;
      border-radius: 4px;
      overflow: hidden;
      margin-top: 8px;
    }
    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
      transition: width 0.3s ease;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
      background: white;
    }
    th {
      background-color: #f8f9fa;
      padding: 12px 16px;
      text-align: left;
      font-weight: 600;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #374151;
      border-bottom: 2px solid #3b82f6;
    }
    td {
      padding: 14px 16px;
      border-bottom: 1px solid #e5e7eb;
      color: #1f2937;
      font-size: 14px;
      background-color: #ffffff;
    }
    tbody tr:hover {
      background-color: #f0f4ff;
    }
    .rank {
      font-weight: 700;
      color: #3b82f6;
      width: 50px;
    }
    .account-name {
      font-weight: 500;
      color: #111827;
    }
    .account-name a {
      color: #3b82f6;
      text-decoration: none;
    }
    .account-name a:hover {
      text-decoration: underline;
      color: #2563eb;
    }
    .status-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      margin-left: 8px;
      vertical-align: middle;
    }
    .status-badge.active {
      background-color: #d1fae5;
      color: #065f46;
    }
    .status-badge.inactive {
      background-color: #fee2e2;
      color: #991b1b;
    }
    .submissions-count {
      text-align: right;
      font-weight: 600;
      color: #111827;
    }
    .comparison-positive {
      color: #10b981;
      font-weight: 600;
    }
    .comparison-negative {
      color: #ef4444;
      font-weight: 600;
    }
    .comparison-neutral {
      color: #6b7280;
      font-weight: 600;
    }
    .section-title {
      color: #111827;
      font-size: 20px;
      font-weight: 700;
      margin: 40px 0 20px 0;
      padding-bottom: 12px;
      border-bottom: 3px solid #3b82f6;
      text-align: center;
    }
    .insights-section h2 {
      color: #111827;
      font-size: 20px;
      font-weight: 700;
      margin-bottom: 20px;
      padding-bottom: 12px;
      border-bottom: 3px solid #3b82f6;
    }
    .footer {
      margin-top: 48px;
      padding-top: 24px;
      border-top: 2px solid #e5e7eb;
      color: #6b7280;
      font-size: 12px;
      text-align: center;
    }
    .footer-logo {
      margin-bottom: 20px;
      text-align: center;
    }
    .footer-logo img {
      height: 48px;
      width: auto;
      display: inline-block;
    }
    .footer a {
      color: #3b82f6;
      text-decoration: none;
    }
    .footer a:hover {
      text-decoration: underline;
    }
    .health-trends-section {
      margin-bottom: 40px;
    }
    .health-trends-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
      margin-top: 20px;
    }
    .health-trend-card {
      background: #ffffff;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }
    .health-trend-card.growing {
      border-left: 4px solid #10b981;
      background: #f0fdf4;
    }
    .health-trend-card.declining {
      border-left: 4px solid #ef4444;
      background: #fef2f2;
    }
    .health-trend-card.new {
      border-left: 4px solid #3b82f6;
      background: #eff6ff;
    }
    .health-trend-card.stable {
      border-left: 4px solid #6b7280;
      background: #f9fafb;
    }
    .health-trend-title {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 12px;
      color: #374151;
    }
    .health-trend-account {
      padding: 8px 0;
      border-bottom: 1px solid #f3f4f6;
      font-size: 13px;
    }
    .health-trend-account:last-child {
      border-bottom: none;
    }
    .health-trend-value {
      color: #6b7280;
      font-size: 12px;
      margin-top: 4px;
    }
    .engagement-chart-section {
      margin-bottom: 40px;
    }
    .chart-container {
      background: #f8f9fa;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      padding: 24px;
      margin-top: 20px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }
    .chart-bars {
      display: flex;
      align-items: flex-end;
      gap: 4px;
      height: 220px;
      padding: 16px 0 40px 0;
      position: relative;
    }
    .chart-bar {
      flex: 1;
      background: linear-gradient(to top, #3b82f6 0%, #2563eb 100%);
      border-radius: 4px 4px 0 0;
      min-height: 2px;
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: center;
    }
    .chart-bar:hover {
      opacity: 0.8;
    }
    .chart-bar-value {
      position: absolute;
      top: -22px;
      font-size: 11px;
      font-weight: 700;
      color: #374151;
      white-space: nowrap;
      text-shadow: 0 1px 2px rgba(255,255,255,0.8);
    }
    .chart-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 8px;
      font-size: 11px;
      color: #6b7280;
    }
    .chart-label {
      flex: 1;
      text-align: center;
    }
    @media print {
      body {
        background-color: white;
        padding: 0;
      }
      .container {
        box-shadow: none;
        padding: 20px;
      }
      .summary-grid, .insights-grid {
        break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="header-logo">
        <a href="https://engagr-adme.netlify.app/" style="display: inline-block; text-decoration: none;">
          <img src="https://engagr-adme.netlify.app/assets/engagr-blk.svg" alt="Engagr Logo" style="height: 40px; width: auto; display: inline-block;" />
        </a>
      </div>
      <h1>Engagr Report</h1>
      <div class="period">
        Reporting Period: ${this.formatDate(startDate.toISOString())} - ${this.formatDate(endDate.toISOString())}
      </div>
    </div>
    
    <div class="summary-grid">
      <div class="summary-card">
        <h3>Total Accounts</h3>
        <p class="value">${this.formatNumber(stats.summary.totalAccounts)}</p>
      </div>
      <div class="summary-card">
        <h3>Total Submissions</h3>
        <p class="value">${this.formatNumber(stats.summary.totalSubmissions)}</p>
      </div>
      <div class="summary-card">
        <h3>Average per Account</h3>
        <p class="value">${this.formatNumber(stats.summary.averageSubmissions)}</p>
      </div>
    </div>
    
    <div class="insights-section">
      <h2>Account Activity Insights</h2>
      <div class="insights-grid">
        <div class="insight-card">
          <h3>Activity Status</h3>
          <div class="insight-row">
            <span class="insight-label">Active Accounts</span>
            <span class="insight-value">${this.formatNumber(activeAccounts)} (${activePercentage}%)</span>
          </div>
          <div class="insight-row">
            <span class="insight-label">Inactive Accounts</span>
            <span class="insight-value">${this.formatNumber(inactiveAccounts)} (${accountActivity.inactivePercentage}%)</span>
          </div>
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${activePercentage}%"></div>
          </div>
          ${inactiveAccounts > 0 && accountActivity.avgDaysSinceLastActivity > 0 ? `
          <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
            <div class="insight-row" style="margin-bottom: 8px;">
              <span class="insight-label">Avg Days Since Last Activity</span>
              <span class="insight-value">${accountActivity.avgDaysSinceLastActivity} days</span>
            </div>
            <div style="font-size: 12px; color: #6b7280; margin-top: 8px;">
              <div class="insight-row" style="margin-bottom: 4px;">
                <span class="insight-label">1-30 days</span>
                <span class="insight-value">${this.formatNumber(accountActivity.inactiveBreakdown.recent)}</span>
              </div>
              <div class="insight-row" style="margin-bottom: 4px;">
                <span class="insight-label">31-60 days</span>
                <span class="insight-value">${this.formatNumber(accountActivity.inactiveBreakdown.moderate)}</span>
              </div>
              <div class="insight-row" style="margin-bottom: 4px;">
                <span class="insight-label">61-90 days</span>
                <span class="insight-value">${this.formatNumber(accountActivity.inactiveBreakdown.long)}</span>
              </div>
              <div class="insight-row">
                <span class="insight-label">90+ days</span>
                <span class="insight-value">${this.formatNumber(accountActivity.inactiveBreakdown.veryLong)}</span>
              </div>
            </div>
          </div>
          ` : ''}
        </div>
        
        <div class="insight-card">
          <h3>Submission Concentration</h3>
          <div class="insight-row">
            <span class="insight-label">Top 20% of Accounts</span>
            <span class="insight-value">${top20Percent}% of submissions</span>
          </div>
          <div class="insight-row">
            <span class="insight-label">Top 5 Accounts</span>
            <span class="insight-value">${top5Percent}% of submissions</span>
          </div>
        </div>
        
        <div class="insight-card">
          <h3>Performance Metrics</h3>
          <div class="insight-row">
            <span class="insight-label">Median Submissions</span>
            <span class="insight-value">${median}</span>
          </div>
          <div class="insight-row">
            <span class="insight-label">Range</span>
            <span class="insight-value">${this.formatNumber(min)} - ${this.formatNumber(max)}</span>
          </div>
          <div class="insight-row">
            <span class="insight-label">Above Average</span>
            <span class="insight-value">${this.formatNumber(aboveAverage)} (${aboveAveragePercentage}%)</span>
          </div>
        </div>
        
        <div class="insight-card">
          <h3>Distribution Breakdown</h3>
          <div class="insight-row">
            <span class="insight-label">0-25 submissions</span>
            <span class="insight-value">${this.formatNumber(distribution.zeroTo25)} accounts</span>
          </div>
          <div class="insight-row">
            <span class="insight-label">26-50 submissions</span>
            <span class="insight-value">${this.formatNumber(distribution.twentySixTo50)} accounts</span>
          </div>
          <div class="insight-row">
            <span class="insight-label">51-100 submissions</span>
            <span class="insight-value">${this.formatNumber(distribution.fiftyOneTo100)} accounts</span>
          </div>
          <div class="insight-row">
            <span class="insight-label">100+ submissions</span>
            <span class="insight-value">${this.formatNumber(distribution.over100)} accounts</span>
          </div>
        </div>
      </div>
    </div>
    
    ${(healthTrends.growing.length > 0 || healthTrends.declining.length > 0 || healthTrends.newAccounts.length > 0 || healthTrends.stable.length > 0) ? `
    <div class="health-trends-section">
      <h2 class="section-title">Account Health Trends</h2>
      <p style="color: #6b7280; font-size: 13px; margin-bottom: 16px;">Accounts showing growth, decline, or new activity</p>
      <div class="health-trends-grid">
        ${healthTrends.growing.length > 0 ? `
        <div class="health-trend-card growing">
          <div class="health-trend-title">Growing</div>
          ${healthTrends.growing.map(account => {
            const accountName = this.escapeHtml(account.accountName || account.formTitle || `Form ${account.formId}`);
            const submissions = account.filteredSubmissions || account.submissions || 0;
            const statusBadge = getStatusBadge(account.status);
            return `
            <div class="health-trend-account">
              <div style="font-weight: 500; color: #111827;">${accountName}${statusBadge}</div>
              <div class="health-trend-value">+${this.formatNumber(submissions)} submissions</div>
            </div>
            `;
          }).join('')}
        </div>
        ` : ''}
        
        ${healthTrends.declining.length > 0 ? `
        <div class="health-trend-card declining">
          <div class="health-trend-title">Declining</div>
          ${healthTrends.declining.map(account => {
            const accountName = this.escapeHtml(account.accountName || account.formTitle || `Form ${account.formId}`);
            const submissions = account.filteredSubmissions || account.submissions || 0;
            const statusBadge = getStatusBadge(account.status);
            return `
            <div class="health-trend-account">
              <div style="font-weight: 500; color: #111827;">${accountName}${statusBadge}</div>
              <div class="health-trend-value">${this.formatNumber(submissions)} submissions (↓)</div>
            </div>
            `;
          }).join('')}
        </div>
        ` : ''}
        
        ${healthTrends.newAccounts.length > 0 ? `
        <div class="health-trend-card new">
          <div class="health-trend-title">New</div>
          ${healthTrends.newAccounts.map(account => {
            const accountName = this.escapeHtml(account.accountName || account.formTitle || `Form ${account.formId}`);
            const submissions = account.filteredSubmissions || account.submissions || 0;
            const statusBadge = getStatusBadge(account.status);
            return `
            <div class="health-trend-account">
              <div style="font-weight: 500; color: #111827;">${accountName}${statusBadge}</div>
              <div class="health-trend-value">${this.formatNumber(submissions)} submissions</div>
            </div>
            `;
          }).join('')}
        </div>
        ` : ''}
        
        ${healthTrends.stable.length > 0 ? `
        <div class="health-trend-card stable">
          <div class="health-trend-title">Stable</div>
          ${healthTrends.stable.map(account => {
            const accountName = this.escapeHtml(account.accountName || account.formTitle || `Form ${account.formId}`);
            const submissions = account.filteredSubmissions || account.submissions || 0;
            const statusBadge = getStatusBadge(account.status);
            return `
            <div class="health-trend-account">
              <div style="font-weight: 500; color: #111827;">${accountName}${statusBadge}</div>
              <div class="health-trend-value">${this.formatNumber(submissions)} submissions</div>
            </div>
            `;
          }).join('')}
        </div>
        ` : ''}
      </div>
    </div>
    ` : ''}
    
    ${dailyData.length > 0 ? `
    <div class="engagement-chart-section">
      <h2 class="section-title">Engagement Trends</h2>
      <p style="color: #6b7280; font-size: 13px; margin-bottom: 16px;">${stats.dateFilter === 'all' ? 'Yearly' : stats.dateFilter === 'last12Months' ? 'Monthly' : 'Daily'} submissions over the reporting period</p>
      <div class="chart-container">
        <div class="chart-bars">
          ${dailyData.map((value, index) => {
            const height = maxDailyValue > 0 ? (value / maxDailyValue * 100) : 0;
            return `<div class="chart-bar" style="height: ${height}%;" title="${chartLabels[index]}: ${this.formatNumber(value)} submissions">
              ${value > 0 ? `<div class="chart-bar-value">${this.formatNumber(value)}</div>` : ''}
            </div>`;
          }).join('')}
        </div>
        <div class="chart-labels">
          ${chartLabels.map((label, index) => {
            // For yearly (all) or monthly (last12Months) data, show all labels
            // For daily data, show every 5th label to avoid crowding
            if (stats.dateFilter === 'all' || stats.dateFilter === 'last12Months') {
              return `<div class="chart-label">${label}</div>`;
            } else {
              const daysToShow = chartLabels.length;
              if (daysToShow <= 7 || index % Math.ceil(daysToShow / 7) === 0 || index === chartLabels.length - 1) {
                return `<div class="chart-label">${label}</div>`;
              }
              return '<div class="chart-label"></div>';
            }
          }).join('')}
        </div>
      </div>
    </div>
    ` : ''}
    
    <div class="section-title">All Accounts by Submissions</div>
    <table>
      <thead>
        <tr>
          <th class="rank">Rank</th>
          <th>Account Name</th>
          <th style="text-align: right;">Submissions</th>
          <th style="text-align: right;">${this.getPreviousPeriodLabel()}</th>
          <th style="text-align: center;">Change</th>
          <th style="text-align: left;">Last Submission</th>
        </tr>
      </thead>
      <tbody>
        ${accountsWithComparison.map((account, index) => {
          const accountName = this.escapeHtml(account.formTitle);
          const statusBadge = getStatusBadge(account.status);
          const dashboardLink = account.accountSlug 
            ? `<a href="https://engagr-adme.netlify.app/account/${account.accountSlug}/dashboard" target="_blank">${accountName}</a>`
            : accountName;
          const currentSubs = account.submissions || 0;
          // null = no real comparison figure available. Must NOT be coerced to 0,
          // which would render a fabricated -100% change.
          const prevSubs = account.previousMonthSubmissions;
          const hasComparison = prevSubs !== null && prevSubs !== undefined;

          const change = hasComparison ? currentSubs - prevSubs : 0;
          // Growth from a zero baseline is undefined, not 100%. Show "New" instead
          // of inventing a percentage.
          const changePercent = hasComparison && prevSubs > 0
            ? Math.round((change / prevSubs) * 100)
            : null;
          const changeColor = !hasComparison ? '#6b7280' : change > 0 ? '#10b981' : change < 0 ? '#ef4444' : '#6b7280';
          const changeIcon = !hasComparison ? '' : change > 0 ? '↑' : change < 0 ? '↓' : '→';

          let changeText;
          if (!hasComparison) {
            changeText = '—';
          } else if (prevSubs === 0) {
            changeText = currentSubs > 0 ? 'New' : 'No change';
          } else if (change === 0) {
            changeText = 'No change';
          } else {
            changeText = `${change > 0 ? '+' : ''}${this.formatNumber(change)} (${change > 0 ? '+' : ''}${changePercent}%)`;
          }
          const prevSubsText = hasComparison ? this.formatNumber(prevSubs) : '—';
          const lastSubmissionDate = account.lastSubmission 
            ? this.formatDate(account.lastSubmission) 
            : 'N/A';
          
          return `
          <tr>
            <td class="rank">${index + 1}</td>
            <td class="account-name">${dashboardLink}${statusBadge}</td>
            <td class="submissions-count">${this.formatNumber(currentSubs)}</td>
            <td class="submissions-count" style="color: #6b7280;">${prevSubsText}</td>
            <td style="text-align: center; color: ${changeColor}; font-weight: 600;">
              ${changeText} ${changeIcon}
            </td>
            <td style="color: #6b7280; font-size: 13px;">${lastSubmissionDate}</td>
          </tr>
          `;
        }).join('')}
        ${accountsWithComparison.length === 0 ? '<tr><td colspan="6" style="text-align: center; color: #6b7280; padding: 32px;">No submissions in this period</td></tr>' : ''}
      </tbody>
    </table>
    
    ${accountsWithComparison.length > 0 ? `
    <p style="margin-top: 16px; color: #6b7280; font-size: 13px; text-align: center;">
      Showing all ${this.formatNumber(stats.accounts.length)} accounts with activity in this period
    </p>
    ` : ''}
    
    <div class="footer">
      <div class="footer-logo">
        <a href="https://engagr-adme.netlify.app/" style="display: inline-block; text-decoration: none;">
          <img src="https://engagr-adme.netlify.app/assets/engagr-blk.svg" alt="Engagr Logo" style="height: 48px; width: auto; display: inline-block;" />
        </a>
      </div>
      <p>This report was generated on ${new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })}</p>
      <p style="margin-top: 8px;">
        For more details and real-time data, visit the 
        <a href="https://engagr-adme.netlify.app/admin">Admin Dashboard</a>
      </p>
    </div>
  </div>
</body>
</html>
      `.trim();
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    viewAccountDetails(account) {
      this.selectedAccount = account;
      this.showAccountModal = true;
    },
    editAccount(account) {
      this.selectedAccount = account;
      this.showAccountModal = true;
    },
    scrollToAccountsTable() {
      const element = document.getElementById('accounts-table');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    closeAccountModal() {
      this.showAccountModal = false;
      this.selectedAccount = null;
    },
    openConfirmModal() {
      if (!this.selectedAccount || !this.selectedAccount.formId) {
        return;
      }
      const currentStatus = this.getAccountStatus(this.selectedAccount);
      this.confirmModalAction = currentStatus === 'active' ? 'deactivate' : 'activate';
      this.showConfirmModal = true;
    },
    closeConfirmModal() {
      this.showConfirmModal = false;
      this.confirmModalAction = null;
    },
    async toggleFormStatus() {
      if (!this.selectedAccount || !this.selectedAccount.formId) {
        this.statusSuccessIsError = true;
        this.showStatusSuccessModal = true;
        this.statusSuccessMessage = 'Error: Form ID not found';
        return;
      }

      const currentStatus = this.getAccountStatus(this.selectedAccount);
      const newStatus = currentStatus === 'active' ? false : true;
      const action = newStatus ? 'activate' : 'deactivate';

      // Store formId and accountName before refresh (in case selectedAccount gets cleared)
      const formId = this.selectedAccount.formId;
      const accountName = this.selectedAccount.accountName || this.selectedAccount.formTitle || `Form ${formId}`;

      // Close confirmation modal
      this.closeConfirmModal();

      this.isTogglingFormStatus = true;
      this.statusSuccessIsError = false;

      try {
        const sessionToken = localStorage.getItem('better-auth.session_token');
        if (!sessionToken) {
          throw new Error('Not authenticated');
        }

        const response = await fetch('/.netlify/functions/update-form-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`
          },
          body: JSON.stringify({
            formId: formId,
            isActive: newStatus
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Failed to update form status');
        }

        // Update selected account status in the modal immediately
        if (this.selectedAccount) {
          this.selectedAccount.is_active = newStatus;
          this.selectedAccount.status = newStatus ? 'active' : 'inactive';
        }

        // Refresh stats to get updated status (cache will be invalidated by backend)
        await this.fetchStats(true); // Force refresh to bypass cache
        
        // Show success notification using stored values
        this.statusSuccessMessage = `${accountName} (Form ${formId}) has been ${action}d successfully!`;
        this.statusSuccessIsError = false;
        this.showStatusSuccessModal = true;
      } catch (error) {
        console.error('Error toggling form status:', error);
        this.statusSuccessMessage = `Error: ${error.message || 'Failed to update form status'}`;
        this.statusSuccessIsError = true;
        this.showStatusSuccessModal = true;
      } finally {
        this.isTogglingFormStatus = false;
      }
    },
    closeStatusSuccessModal() {
      this.showStatusSuccessModal = false;
      this.statusSuccessMessage = '';
      this.statusSuccessIsError = false;
    }
  }
};
</script>

<style scoped>
.bg-image-animate {
  animation: subtle-zoom 30s ease-in-out infinite;
}

@keyframes subtle-zoom {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

/* Tooltip styles */
.tooltip-trigger {
  position: relative;
}

.tooltip-content {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;
  z-index: 50;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
}

.tooltip-trigger:hover .tooltip-content {
  opacity: 1;
}

.tooltip-content::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.95);
}
</style>


